import { useState, useEffect, useRef, Component } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthCtx } from '../App';
import api from '../utils/api';
import { formatDate, statusBadge, capitalize } from '../utils/helpers';
import { useCurrency } from '../hooks/useCurrency';
import { toast } from 'react-toastify';
import MarketingPage from '../components/admin/MarketingPage';
import CouponsPage from '../components/admin/CouponsPage';
import AnalyticsPage from '../components/admin/AnalyticsPage';
import BackupExport from '../components/admin/BackupExport';
import ForecastPage from '../components/admin/ForecastPage';
import PriceSchedulerPage from '../components/admin/PriceSchedulerPage';
import BlogManager from '../components/admin/BlogManager';
import AmbassadorInvites from '../components/admin/AmbassadorInvites';
import ReferralQRModal from '../components/ReferralQRModal';
import { useCategories, refreshCategories } from '../hooks/useCategories';
import { ICON_KEYS, DEFAULT_GRAD, DEFAULT_INK } from '../constants/categories';
import { Icon, StatusDot, BrandMark } from '../components/admin/AdminIcons';
import CategoryIcon from '../components/CategoryIcon';

// ─── Section error boundary ───────────────────────────────────────────────────
class SectionErrorBoundary extends Component {
  constructor(props) { super(props); this.state = { crashed: false }; }
  static getDerivedStateFromError() { return { crashed: true }; }
  reset() { this.setState({ crashed: false }); }
  render() {
    if (this.state.crashed) {
      return (
        <div className="py-20 text-center">
          <Icon name="warning" className="w-12 h-12 mx-auto mb-4 text-rose-400" />
          <div className="font-bold text-gray-700 text-lg mb-2">Something went wrong in this section</div>
          <div className="text-gray-400 text-sm mb-6">Other sections are unaffected. Try reloading this one.</div>
          <button onClick={() => this.reset()} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
            Reload section
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function BoundedRoute({ element }) {
  return <SectionErrorBoundary>{element}</SectionErrorBoundary>;
}

// ─── Dashboard building blocks ────────────────────────────────────────────────

// Compact trend chip: ▲ +12%  /  ▼ -4%  vs a previous value.
function Trend({ current, previous, suffix = '' }) {
  if (previous == null) return null;
  if (previous === 0) {
    if (!current) return <span className="text-[11px] text-gray-400">—</span>;
    return <span className="text-[11px] font-semibold text-emerald-600">▲ new</span>;
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return <span className="text-[11px] text-gray-400">▬ 0%{suffix}</span>;
  const up = pct > 0;
  return (
    <span className={'text-[11px] font-semibold ' + (up ? 'text-emerald-600' : 'text-rose-500')}>
      {up ? '▲' : '▼'} {up ? '+' : ''}{pct}%{suffix}
    </span>
  );
}

// Small KPI tile — soft white card with a tinted icon chip (Fynix style).
function Tile({ icon, label, value, sub, trend, accent = 'slate' }) {
  const chip = {
    slate: 'bg-gray-100 text-gray-500',
    emerald: 'bg-emerald-50 text-emerald-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    amber: 'bg-amber-50 text-amber-600',
    rose: 'bg-tea-50 text-tea-600',
    sky: 'bg-sky-50 text-sky-600',
  }[accent] || 'bg-gray-100 text-gray-500';
  return (
    <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_2px_12px_rgba(17,12,46,0.04)] flex flex-col gap-2 hover:shadow-[0_6px_20px_rgba(219,39,119,0.08)] transition-shadow">
      <div className="flex items-center justify-between">
        <span className={'grid place-items-center w-9 h-9 rounded-xl ' + chip}><Icon name={icon} className="w-[18px] h-[18px]" /></span>
        {trend}
      </div>
      <div className="text-2xl font-extrabold text-gray-900 leading-tight tabular-nums">{value}</div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold">{label}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  );
}

// Horizontal meter: a labelled value bar filling toward a max.
function Meter({ label, value, max, display, color = 'bg-tea-500', hint }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="font-medium text-gray-600 truncate">{label}</span>
        <span className="font-bold text-gray-800 tabular-nums">{display}</span>
      </div>
      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={'h-full rounded-full transition-all duration-700 ' + color} style={{ width: pct + '%' }} />
      </div>
      {hint && <div className="text-[10px] text-gray-400 mt-0.5">{hint}</div>}
    </div>
  );
}

// Circular goal gauge (SVG) — today vs a target, 0–100%+.
function Gauge({ pct, label, center, color = '#10b981' }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const r = 42, c = 2 * Math.PI * r;
  const off = c - (clamped / 100) * c;
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
        <circle cx="50" cy="50" r={r} fill="none" stroke="#eef2f5" strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          style={{ transition: 'stroke-dashoffset 0.8s ease' }} />
      </svg>
      <div className="-mt-16 mb-6 text-center">
        <div className="text-lg font-extrabold text-gray-900 tabular-nums">{center}</div>
      </div>
      <div className="text-[11px] uppercase tracking-wide text-gray-500 font-semibold text-center">{label}</div>
    </div>
  );
}

// 14-day revenue sparkline (inline SVG bars).
function Sparkline({ series, fmt }) {
  if (!series || series.length === 0) return <div className="text-xs text-gray-400">No revenue yet.</div>;
  const max = Math.max(...series.map(d => d.revenueCents), 1);
  return (
    <div className="flex items-end gap-1 h-24">
      {series.map((d) => {
        const h = Math.max(3, Math.round((d.revenueCents / max) * 100));
        return (
          <div key={d.day} className="flex-1 group relative flex flex-col justify-end items-center">
            <div className="w-full bg-tea-400 hover:bg-tea-600 rounded-t transition-colors" style={{ height: h + '%' }} />
            <div className="absolute -top-9 hidden group-hover:block bg-gray-900 text-white text-[10px] rounded px-1.5 py-1 whitespace-nowrap z-10">
              {d.day.slice(5)}: {fmt(d.revenueCents)} · {d.orders} ord
            </div>
          </div>
        );
      })}
    </div>
  );
}

// A ranked leaderboard card.
function Leaderboard({ title, icon, rows, render, empty }) {
  return (
    <div className="bg-white rounded-xl border p-4 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon name={icon} className="w-[18px] h-[18px] text-tea-500" />
        <span className="text-sm font-bold text-gray-700">{title}</span>
      </div>
      {(!rows || rows.length === 0) ? (
        <div className="text-xs text-gray-400 py-4 text-center">{empty}</div>
      ) : (
        <ol className="space-y-2">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2">
              <span className={'w-5 h-5 shrink-0 rounded-full text-[10px] font-bold flex items-center justify-center ' +
                (i === 0 ? 'bg-amber-400 text-white' : i === 1 ? 'bg-gray-300 text-white' : i === 2 ? 'bg-amber-700 text-white' : 'bg-gray-100 text-gray-500')}>
                {i + 1}
              </span>
              {render(r)}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

const SECTION_KEYS = ['today', 'meters', 'trend', 'leaderboards', 'operations'];

// ─── Overview metrics ─────────────────────────────────────────────────────────
function MetricsOverview() {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [auto, setAuto] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [show, setShow] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('adminOverviewSections') || '{}');
      return SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: saved[k] !== false }), {});
    } catch { return SECTION_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {}); }
  });
  const timer = useRef(null);

  const toggle = (k) => setShow(prev => {
    const next = { ...prev, [k]: !prev[k] };
    try { localStorage.setItem('adminOverviewSections', JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  const load = (quiet = false) => {
    if (!quiet) { setLoading(true); setLoadError(false); }
    api.get('/admin/dashboard')
      .then(res => { setData(res.data); setUpdatedAt(new Date()); setLoadError(false); })
      .catch(() => { if (!quiet) { toast.error('Failed to load dashboard'); setLoadError(true); } })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (auto) {
      timer.current = setInterval(() => load(true), 20000);
      return () => clearInterval(timer.current);
    }
  }, [auto]);

  if (loading) {
    return <div className="py-12 text-center text-gray-400">Loading dashboard…</div>;
  }
  if (loadError || !data) {
    return (
      <div className="py-12 text-center">
        <Icon name="warning" className="w-10 h-10 mx-auto mb-3 text-amber-400" />
        <div className="text-gray-600 font-medium mb-4">Could not load dashboard</div>
        <button onClick={() => load()} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
          Retry
        </button>
      </div>
    );
  }

  const { today, totals, statusBreakdown, revenueSeries, topSpenders, topAffiliates, topCoupons, topProducts, lowStock, live } = data;

  // Daily average revenue over the last 30d → today's "goal".
  const dailyAvg = totals.orders30d > 0 ? Math.round(totals.revenue30dCents / 30) : 0;
  const goalPct = dailyAvg > 0 ? Math.round((today.revenueCents / dailyAvg) * 100) : (today.revenueCents > 0 ? 100 : 0);
  const statusMax = Math.max(1, ...statusBreakdown.map(s => s.count));
  const STATUS_COLOR = {
    delivered: 'bg-emerald-500', shipped: 'bg-sky-500', processing: 'bg-indigo-500',
    paid: 'bg-tea-500', pending: 'bg-amber-400', cancelled: 'bg-gray-400', refunded: 'bg-rose-400',
  };

  // Composite "finance score" — how today's revenue tracks the 30-day daily avg.
  const score = Math.min(99, Math.max(8, goalPct || 0));
  const scoreLabel = score >= 90 ? 'Excellent' : score >= 70 ? 'Great' : score >= 45 ? 'Steady' : 'Building';

  return (
    <div>
      {/* ── Fynix-style hero row ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-6">
        {/* Pink gradient balance / revenue card */}
        <div className="lg:col-span-5 relative overflow-hidden rounded-3xl p-6 text-white shadow-[0_18px_40px_rgba(219,39,119,0.28)]"
          style={{ background: 'linear-gradient(135deg, #f9a8d0 0%, #ec4899 42%, #be185d 100%)' }}>
          {/* decorative swirls */}
          <div className="absolute -right-10 -top-12 h-44 w-44 rounded-full bg-white/15 blur-md" />
          <div className="absolute right-12 bottom-2 h-28 w-28 rounded-full bg-white/10 blur-lg" />
          <div className="absolute -left-8 bottom-6 h-24 w-24 rounded-full bg-white/10 blur-md" />
          <div className="relative">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-sm font-medium text-white/80">Total Revenue</div>
                <div className="mt-1 flex items-end gap-2">
                  <span className="text-4xl font-extrabold tracking-tight tabular-nums">{fmt(totals.revenueCents)}</span>
                  <span className="mb-1.5 text-xs font-semibold text-white/70">USD</span>
                </div>
              </div>
              <Link to="/admin/products" aria-label="Add product"
                className="grid place-items-center h-9 w-9 rounded-full bg-white/25 hover:bg-white/40 transition-colors text-xl leading-none">+</Link>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 rounded-full px-3 py-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              {live} live now · {totals.orders.toLocaleString()} orders all-time
            </div>
            <div className="mt-6 flex items-center gap-2">
              <button onClick={() => load()}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-white text-tea-700 text-sm font-bold px-4 py-2.5 hover:bg-tea-50 transition-colors">
                <Icon name="pulse" className="w-4 h-4" /> Refresh
              </button>
              <button onClick={() => setAuto(a => !a)}
                className={'flex-1 inline-flex items-center justify-center gap-2 rounded-full text-sm font-bold px-4 py-2.5 transition-colors ' +
                  (auto ? 'bg-tea-900/40 text-white ring-1 ring-white/40' : 'bg-white/20 text-white hover:bg-white/30')}>
                <StatusDot color={auto ? 'bg-emerald-300' : 'bg-white/50'} /> {auto ? 'Auto on' : 'Auto off'}
              </button>
            </div>
            {updatedAt && <div className="mt-2 text-[11px] text-white/60">Updated {updatedAt.toLocaleTimeString()}</div>}
          </div>
        </div>

        {/* Today at a glance — three mini stats */}
        <div className="lg:col-span-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_2px_12px_rgba(17,12,46,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <span className="text-base font-bold text-gray-900">Today at a glance</span>
            <span className="text-[11px] text-gray-400">vs yesterday</span>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[
              { ic: 'revenue', tint: 'bg-tea-50 text-tea-600', label: 'Revenue', val: fmt(today.revenueCents), cur: today.revenueCents, prev: today.prevRevenueCents },
              { ic: 'orders', tint: 'bg-sky-50 text-sky-600', label: 'Orders', val: today.orders, cur: today.orders, prev: today.prevOrders },
              { ic: 'views', tint: 'bg-indigo-50 text-indigo-600', label: 'Views', val: today.views.toLocaleString(), cur: today.views, prev: today.prevViews },
            ].map(s => (
              <div key={s.label} className="rounded-2xl border border-gray-100 p-3">
                <span className={'grid place-items-center w-8 h-8 rounded-lg mb-2 ' + s.tint}><Icon name={s.ic} className="w-4 h-4" /></span>
                <div className="text-lg font-extrabold text-gray-900 tabular-nums leading-tight truncate">{s.val}</div>
                <div className="flex items-center justify-between mt-0.5">
                  <span className="text-[11px] text-gray-400">{s.label}</span>
                  <Trend current={s.cur} previous={s.prev} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Finance score */}
        <div className="lg:col-span-3 rounded-3xl border border-gray-100 bg-white p-6 shadow-[0_2px_12px_rgba(17,12,46,0.04)] flex flex-col">
          <div className="flex items-center justify-between">
            <span className="text-base font-bold text-gray-900">Store Score</span>
            <span className="text-gray-300 text-xl leading-none">···</span>
          </div>
          <div className="text-[11px] text-gray-400 mt-1">Today vs 30-day pace</div>
          <div className="mt-auto pt-4 flex items-end justify-between">
            <span className="text-2xl font-extrabold text-tea-600">{scoreLabel}</span>
            <span className="text-2xl font-extrabold text-gray-900 tabular-nums">{score}%</span>
          </div>
          <div className="mt-3 h-2.5 w-full rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full rounded-full bg-gradient-to-r from-tea-400 to-tea-600 transition-all duration-700" style={{ width: score + '%' }} />
          </div>
        </div>
      </div>

      {/* Section toggles */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          ['today', 'pulse', "Today's pulse"], ['meters', 'meters', 'Meters'], ['trend', 'forecast', 'Trend'],
          ['leaderboards', 'trophy', 'Leaderboards'], ['operations', 'operations', 'Operations'],
        ].map(([k, ic, label]) => (
          <button key={k} onClick={() => toggle(k)}
            className={'inline-flex items-center gap-1.5 text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors ' +
              (show[k] ? 'bg-tea-50 text-tea-700 border-tea-200' : 'bg-white text-gray-400 border-gray-200 hover:border-gray-300')}>
            <Icon name={ic} className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {/* Today's pulse */}
      {show.today && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-7">
          <Tile icon="revenue" label="Revenue today" value={fmt(today.revenueCents)} accent="emerald"
            trend={<Trend current={today.revenueCents} previous={today.prevRevenueCents} />}
            sub="vs yesterday" />
          <Tile icon="orders" label="Orders today" value={today.orders} accent="sky"
            trend={<Trend current={today.orders} previous={today.prevOrders} />} sub="vs yesterday" />
          <Tile icon="views" label="Views today" value={today.views.toLocaleString()} accent="indigo"
            trend={<Trend current={today.views} previous={today.prevViews} />}
            sub={`${today.visitors.toLocaleString()} unique`} />
          <Tile icon="newCustomer" label="New customers" value={today.newCustomers} accent="amber" sub="signed up today" />
          <Tile icon="live" label="Live visitors" value={live} accent="emerald" sub="on the site now" />
          <Tile icon="receipt" label="Avg order value" value={fmt(totals.aovCents)} accent="slate" sub="all time" />
        </div>
      )}

      {/* Meters & gauges */}
      {show.meters && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border p-5 shadow-sm flex items-center justify-around">
            <Gauge pct={goalPct} label="Today vs daily avg" center={goalPct + '%'}
              color={goalPct >= 100 ? '#10b981' : goalPct >= 60 ? '#f59e0b' : '#f43f5e'} />
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
            <Meter label="Today's revenue → daily avg goal" value={today.revenueCents} max={Math.max(dailyAvg, today.revenueCents)}
              display={fmt(today.revenueCents)} color="bg-emerald-500"
              hint={`Daily avg (30d): ${fmt(dailyAvg)}`} />
            <Meter label="Pending commissions" value={totals.pendingCommissionsCents} max={Math.max(totals.walletLiabilityCents, totals.pendingCommissionsCents, 1)}
              display={fmt(totals.pendingCommissionsCents)} color="bg-amber-500"
              hint="Owed to affiliates, not yet paid" />
            <Meter label="Wallet liability" value={totals.walletLiabilityCents} max={Math.max(totals.walletLiabilityCents, totals.pendingCommissionsCents, 1)}
              display={fmt(totals.walletLiabilityCents)} color="bg-rose-500"
              hint="Total positive wallet balances" />
          </div>
        </div>
      )}

      {/* 14-day trend + order status funnel */}
      {show.trend && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-7">
          <div className="bg-white rounded-xl border p-5 shadow-sm lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-gray-700"><Icon name="forecast" className="w-4 h-4 text-tea-500" /> Revenue — last 14 days</span>
              <span className="text-xs text-gray-400">{fmt(totals.revenue30dCents)} in 30d · {totals.orders30d} orders</span>
            </div>
            <Sparkline series={revenueSeries} fmt={fmt} />
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-gray-700 mb-3"><Icon name="analytics" className="w-4 h-4 text-tea-500" /> Orders by status</span>
            <div className="space-y-2.5">
              {statusBreakdown.sort((a, b) => b.count - a.count).map(s => (
                <Meter key={s.status} label={capitalize(s.status)} value={s.count} max={statusMax}
                  display={s.count} color={STATUS_COLOR[s.status] || 'bg-gray-400'} />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Leaderboards */}
      {show.leaderboards && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-7">
          <Leaderboard title="Biggest spenders" icon="gem" rows={topSpenders} empty="No orders yet."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.name}</div>
                  <div className="text-[11px] text-gray-400">{r.orders} orders</div>
                </div>
                <span className="text-sm font-bold text-emerald-600 tabular-nums whitespace-nowrap">{fmt(r.spentCents)}</span>
              </div>
            )} />
          <Leaderboard title="Top affiliates" icon="trophy" rows={topAffiliates} empty="No commissions yet."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">
                    {r.name}{r.tier === 'platinum' && <Icon name="sparkles" title="Platinum" className="inline w-3.5 h-3.5 ml-1 text-amber-500" />}
                  </div>
                  <div className="text-[11px] text-gray-400">{r.downline} downline · {r.commissions} comms</div>
                </div>
                <span className="text-sm font-bold text-indigo-600 tabular-nums whitespace-nowrap">{fmt(r.earnedCents)}</span>
              </div>
            )} />
          <Leaderboard title="Best coupons" icon="coupons" rows={topCoupons} empty="No coupons redeemed."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-mono font-semibold text-gray-800 truncate">{r.code}</div>
                  <div className="text-[11px] text-gray-400">{r.uses} uses · {fmt(r.discountCents)} off</div>
                </div>
                <span className="text-sm font-bold text-amber-600 tabular-nums whitespace-nowrap">{fmt(r.revenueCents)}</span>
              </div>
            )} />
          <Leaderboard title="Top products" icon="flame" rows={topProducts} empty="No sales yet."
            render={(r) => (
              <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{r.name}</div>
                  <div className="text-[11px] text-gray-400">{r.units} units</div>
                </div>
                <span className="text-sm font-bold text-tea-600 tabular-nums whitespace-nowrap">{fmt(r.revenueCents)}</span>
              </div>
            )} />
        </div>
      )}

      {/* Operations */}
      {show.operations && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-xl border p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <span className="inline-flex items-center gap-2 text-sm font-bold text-gray-700"><Icon name="analytics" className="w-4 h-4 text-amber-500" /> Low stock alerts</span>
              <span className="text-[11px] text-gray-400">≤ 10 units in stock</span>
            </div>
            {(!lowStock || lowStock.length === 0) ? (
              <div className="inline-flex items-center justify-center gap-1.5 w-full text-xs text-gray-400 py-4"><Icon name="check" className="w-4 h-4 text-emerald-500" /> All variants well stocked.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {lowStock.map((p, i) => (
                  <div key={i} className={'flex items-center justify-between gap-2 rounded-lg px-3 py-2 border ' +
                    (p.inventory <= 0 ? 'bg-rose-50 border-rose-200' : p.inventory <= 3 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200')}>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-800 truncate">{p.name}</div>
                      <div className="text-[10px] text-gray-400 font-mono">{p.sku}</div>
                    </div>
                    <span className={'text-sm font-bold tabular-nums ' + (p.inventory <= 0 ? 'text-rose-600' : 'text-amber-600')}>
                      {p.inventory <= 0 ? 'OUT' : p.inventory}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white rounded-xl border p-5 shadow-sm flex flex-col gap-3">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-gray-700"><Icon name="calculator" className="w-4 h-4 text-tea-500" /> Lifetime totals</span>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total revenue</span>
              <span className="font-bold text-gray-900 tabular-nums">{fmt(totals.revenueCents)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-500">Total orders</span>
              <span className="font-bold text-gray-900 tabular-nums">{totals.orders.toLocaleString()}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Orders management ────────────────────────────────────────────────────────
function AdminOrderChatModal({ order, onClose }) {
  const { fmt } = useCurrency({ usd: true });
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/orders/${order.id}/messages`);
      setMessages(data.messages || []);
    } catch (err) {
      // silent
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/orders/${order.id}/messages`, { body: text });
      setMessages(m => [...m, data]);
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not send message');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col overflow-hidden"
        style={{ maxHeight: '85vh' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Live chat</div>
            <div className="font-bold text-gray-900">
              {order.customer?.firstName} {order.customer?.lastName}
              <span className="text-gray-400 font-mono text-xs ml-2">#{order.id.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="text-xs text-gray-500 mt-0.5">
              {capitalize(order.paymentMethod)} · {fmt(order.totalInCents)} · {capitalize(order.status)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
        </div>

        <div ref={scrollerRef} className="flex-1 overflow-y-auto bg-gray-50 p-5 space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-gray-400 text-sm italic py-10">
              No messages yet. Send the first reply below.
            </div>
          )}
          {messages.map(m => {
            const fromAdmin = m.senderType === 'admin';
            return (
              <div key={m.id} className={'flex ' + (fromAdmin ? 'justify-end' : 'justify-start')}>
                <div className={
                  'max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed shadow-sm ' +
                  (fromAdmin
                    ? 'bg-tea-600 text-white rounded-br-sm'
                    : m.senderType === 'system'
                      ? 'bg-gray-200 text-gray-600 italic'
                      : 'bg-white text-gray-900 border border-gray-200 rounded-bl-sm')
                }>
                  <div className={'text-[10px] uppercase tracking-wider mb-1 ' + (fromAdmin ? 'text-tea-100' : 'text-gray-400')}>
                    {fromAdmin ? (m.author?.firstName || 'You') : (m.senderType === 'customer' ? 'Customer' : 'System')}
                    <span className="ml-2 opacity-80">{new Date(m.createdAt).toLocaleTimeString()}</span>
                  </div>
                  <div className="whitespace-pre-wrap break-words">{m.body}</div>
                  {m.isPaymentClaim && (
                    <div className={'mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold ' + (fromAdmin ? 'text-amber-200' : 'text-amber-600')}>
                      <Icon name="sparkles" className="w-3 h-3" /> Payment confirmation
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="border-t bg-white p-4 flex items-end gap-2">
          <textarea
            rows={2}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Reply to the customer…"
            className="flex-1 resize-none rounded-lg border border-gray-300 focus:border-tea-500 focus:ring-1 focus:ring-tea-200 focus:outline-none text-sm px-3 py-2"
            maxLength={2000}
          />
          <button
            onClick={send}
            disabled={!draft.trim() || sending}
            className="shrink-0 px-5 py-2.5 rounded-lg bg-tea-600 text-white text-sm font-semibold hover:bg-tea-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Floating live-chat dock (visible across the whole admin area) ────────────
// Polls the unified threads feed: orders with a live customer and/or an unread
// customer message. Surfaces toasts when someone new needs attention, and opens
// the existing chat modal so the admin can reply from anywhere in admin.
function AdminChatDock() {
  const { fmt } = useCurrency({ usd: true });
  const [threads, setThreads] = useState([]);
  const [counts, setCounts] = useState({ unreadCount: 0, liveCount: 0 });
  const [open, setOpen] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);

  const initializedRef = useRef(false);
  const prevUnreadRef = useRef({});   // orderId -> unread count from last poll
  const prevLiveRef = useRef(new Set()); // orderIds that were live last poll

  const load = async () => {
    try {
      const { data } = await api.get('/admin/chat/threads');
      const next = data.threads || [];
      setThreads(next);
      setCounts({ unreadCount: data.unreadCount || 0, liveCount: data.liveCount || 0 });

      // Diff against the previous poll to fire notifications (skip the very
      // first load so we don't toast a backlog on mount).
      const liveNow = new Set();
      const unreadNow = {};
      next.forEach(t => {
        unreadNow[t.order.id] = t.unread;
        if (t.customerPresent) liveNow.add(t.order.id);
      });

      if (initializedRef.current) {
        next.forEach(t => {
          const name = t.order.customer
            ? `${t.order.customer.firstName || ''} ${t.order.customer.lastName || ''}`.trim() || 'A customer'
            : 'A customer';
          const tag = '#' + t.order.id.slice(0, 8).toUpperCase();
          const prevUnread = prevUnreadRef.current[t.order.id] || 0;
          if (t.unread > prevUnread) {
            toast.info(`New message from ${name} (${tag})`);
          } else if (t.customerPresent && !prevLiveRef.current.has(t.order.id)) {
            toast(`${name} is live on ${tag} — they may want a reply`);
          }
        });
      }

      prevUnreadRef.current = unreadNow;
      prevLiveRef.current = liveNow;
      initializedRef.current = true;
    } catch {
      // silent — transient poll failures are fine
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalBadge = counts.unreadCount + counts.liveCount;

  return (
    <>
      {activeOrder && (
        <AdminOrderChatModal
          order={activeOrder}
          onClose={() => { setActiveOrder(null); load(); }}
        />
      )}

      <div className="fixed bottom-20 right-6 md:bottom-6 z-40 flex flex-col items-end">
        {open && (
          <div className="mb-3 w-80 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden flex flex-col" style={{ maxHeight: '28rem' }}>
            <div className="px-4 py-3 bg-gradient-to-r from-amber-50 to-orange-50 border-b flex items-center justify-between">
              <div className="font-bold text-gray-900 text-sm">Live customer chats</div>
              <div className="flex items-center gap-2 text-[11px] font-semibold">
                {counts.liveCount > 0 && <span className="inline-flex items-center gap-1 text-green-600"><StatusDot color="bg-green-500" /> {counts.liveCount} live</span>}
                {counts.unreadCount > 0 && <span className="text-red-600">{counts.unreadCount} unread</span>}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto divide-y">
              {threads.length === 0 ? (
                <div className="text-center text-gray-400 text-sm italic py-10 px-4">
                  No active chats. You'll be notified when a customer needs you.
                </div>
              ) : (
                threads.map(t => {
                  const name = t.order.customer
                    ? `${t.order.customer.firstName || ''} ${t.order.customer.lastName || ''}`.trim() || 'Customer'
                    : 'Customer';
                  return (
                    <button
                      key={t.order.id}
                      onClick={() => { setActiveOrder(t.order); setOpen(false); }}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex flex-col gap-1"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm text-gray-900 truncate">{name}</span>
                        <span className="flex items-center gap-1.5 shrink-0">
                          {t.customerPresent && (
                            <span className="inline-block w-2 h-2 rounded-full bg-green-500" style={{ boxShadow: '0 0 6px #22c55e' }} />
                          )}
                          {t.unread > 0 && (
                            <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">
                              {t.unread}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-gray-400">
                        <span className="font-mono">#{t.order.id.slice(0, 8).toUpperCase()}</span>
                        <span>·</span>
                        <span>{fmt(t.order.totalInCents)}</span>
                        <span>·</span>
                        <span>{capitalize(t.order.status)}</span>
                      </div>
                      {t.lastMessage && (
                        <div className="text-xs text-gray-500 truncate">
                          {t.lastMessage.senderType === 'admin' ? 'You: ' : ''}{t.lastMessage.body}
                        </div>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        <button
          onClick={() => setOpen(o => !o)}
          className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center bg-tea-600 hover:bg-tea-700 text-white hover:scale-105 transition-transform"
          aria-label="Live customer chats"
        >
          <Icon name="chat" className="w-6 h-6" />
          {totalBadge > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center">
              {totalBadge}
            </span>
          )}
        </button>
      </div>
    </>
  );
}

function OrdersList() {
  const { fmt } = useCurrency({ usd: true });
  const [orders, setOrders]               = useState([]);
  const [page, setPage]                   = useState(1);
  const [pagination, setPagination]       = useState({});
  const [trackingInputs, setTrackingInputs] = useState({}); // { orderId: { number, carrier } }
  const [editingTracking, setEditingTracking] = useState(new Set());
  const [chatOrder, setChatOrder]         = useState(null);
  const deleteTimers = useRef({}); // orderId -> setTimeout id for the pending (undoable) delete

  useEffect(() => {
    api.get('/admin/orders', { params: { page } })
      .then(res => { setOrders(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load orders'));
  }, [page]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch('/admin/orders/' + id + '/status', { status });
      setOrders(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      toast.success('Order updated');
    } catch {
      toast.error('Failed to update order');
    }
  };

  const setTrackingField = (id, field, value) => {
    setTrackingInputs(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const saveTracking = async (id) => {
    const input = trackingInputs[id] || {};
    if (!input.number?.trim()) { toast.error('Tracking number is required'); return; }
    try {
      const res = await api.patch('/admin/orders/' + id + '/tracking', {
        trackingNumber: input.number.trim(),
        carrier: input.carrier?.trim() || undefined,
      });
      setOrders(prev => prev.map(o =>
        o.id === id ? { ...o, trackingNumber: res.data.trackingNumber, carrier: res.data.carrier, status: res.data.status } : o
      ));
      setTrackingInputs(prev => { const n = { ...prev }; delete n[id]; return n; });
      setEditingTracking(prev => { const s = new Set(prev); s.delete(id); return s; });
      toast.success('Tracking saved — customer has been notified');
    } catch {
      toast.error('Failed to save tracking');
    }
  };

  const startEdit = (o) => {
    setTrackingInputs(prev => ({ ...prev, [o.id]: { number: o.trackingNumber || '', carrier: o.carrier || '' } }));
    setEditingTracking(prev => new Set([...prev, o.id]));
  };

  // Soft UX, hard delete: pull the row out of the list immediately and show an
  // Undo toast. The real (irreversible) DELETE only fires after the window lapses,
  // so Undo just cancels the timer and drops the row back into place — no server
  // restore needed. The cleanup (commission clawback, restock) still runs server-side.
  const UNDO_WINDOW_MS = 6000;
  const deleteOrder = (o) => {
    const index = orders.findIndex(x => x.id === o.id);
    setOrders(prev => prev.filter(x => x.id !== o.id));

    const reinsert = () => setOrders(prev =>
      prev.some(x => x.id === o.id) ? prev
        : [...prev.slice(0, index), o, ...prev.slice(index)]
    );

    const undo = () => {
      clearTimeout(deleteTimers.current[o.id]);
      delete deleteTimers.current[o.id];
      reinsert();
    };

    toast(({ closeToast }) => (
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm">Order deleted</span>
        <button
          onClick={() => { undo(); closeToast(); }}
          className="text-xs font-bold text-tea-700 hover:text-tea-900 underline"
        >Undo</button>
      </div>
    ), { autoClose: UNDO_WINDOW_MS, closeOnClick: false, pauseOnHover: false, closeButton: false });

    deleteTimers.current[o.id] = setTimeout(async () => {
      delete deleteTimers.current[o.id];
      try {
        const res = await api.delete('/admin/orders/' + o.id);
        const clawed = res.data?.clawedBackCents;
        if (clawed) toast.info(`${fmt(clawed)} clawed back from uplines`);
      } catch (err) {
        reinsert();
        toast.error(err.response?.data?.error || 'Failed to delete order');
      }
    }, UNDO_WINDOW_MS);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Orders</h2>
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['ID', 'Customer', 'Total', 'Payment', 'Status', 'Tracking', 'Date', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              const input = trackingInputs[o.id] || {};
              const isEditing = editingTracking.has(o.id);
              const showForm = !o.trackingNumber || isEditing;

              return (
                <tr key={o.id} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{o.id.slice(0, 8)}</td>
                  <td className="px-4 py-3">{o.customer?.firstName} {o.customer?.lastName}</td>
                  <td className="px-4 py-3 font-medium">{fmt(o.totalInCents)}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">Card</td>
                  <td className="px-4 py-3">
                    <span className={statusBadge(o.status)}>{capitalize(o.status)}</span>
                  </td>
                  <td className="px-4 py-3">
                    {showForm ? (
                      <div className="flex gap-1 items-center">
                        <input
                          type="text"
                          placeholder="Tracking #"
                          value={input.number || ''}
                          onChange={e => setTrackingField(o.id, 'number', e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveTracking(o.id)}
                          className="text-xs border border-gray-200 rounded px-2 py-1 w-28 focus:outline-none focus:border-tea-400"
                        />
                        <input
                          type="text"
                          placeholder="Carrier"
                          value={input.carrier || ''}
                          onChange={e => setTrackingField(o.id, 'carrier', e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && saveTracking(o.id)}
                          className="text-xs border border-gray-200 rounded px-2 py-1 w-20 focus:outline-none focus:border-tea-400"
                        />
                        <button
                          onClick={() => saveTracking(o.id)}
                          className="text-xs bg-tea-600 text-white rounded px-2 py-1 hover:bg-tea-700 whitespace-nowrap">
                          Ship &amp; Notify
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => { setEditingTracking(prev => { const s = new Set(prev); s.delete(o.id); return s; }); }}
                            className="text-xs text-gray-400 hover:text-gray-600">
                            Cancel
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-mono text-xs font-medium text-gray-800">{o.trackingNumber}</div>
                          {o.carrier && <div className="text-xs text-gray-400">{o.carrier}</div>}
                        </div>
                        <button
                          onClick={() => startEdit(o)}
                          className="text-xs text-tea-600 hover:text-tea-800 underline">
                          Edit
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{formatDate(o.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1.5 items-start">
                      <select
                        value={o.status}
                        onChange={e => updateStatus(o.id, e.target.value)}
                        className="text-xs border border-gray-200 rounded px-2 py-1">
                        {['processing', 'shipped', 'delivered', 'cancelled', 'refunded'].map(s => (
                          <option key={s} value={s}>{capitalize(s)}</option>
                        ))}
                      </select>
                      <button
                        onClick={() => deleteOrder(o)}
                        title="Delete this order (with undo)"
                        className="text-[10px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 transition-colors">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex gap-2 mt-4 justify-end">
        {[...Array(pagination.pages || 1)].map((_, i) => (
          <button key={i} onClick={() => setPage(i + 1)}
            className={'px-3 py-1 rounded border text-sm ' +
              (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
            {i + 1}
          </button>
        ))}
      </div>

      {chatOrder && (
        <AdminOrderChatModal order={chatOrder} onClose={() => setChatOrder(null)} />
      )}
    </div>
  );
}

// ─── Users management (god-tier) ──────────────────────────────────────────────
const ROLE_BADGE = {
  admin:      'bg-purple-100 text-purple-800',
  ambassador: 'bg-emerald-100 text-emerald-800',
  affiliate:  'bg-blue-100 text-blue-800',
  vip:        'bg-amber-100 text-amber-800',
  customer:   'bg-gray-100 text-gray-700',
};

// Selectable account types and their human labels. "vip" = Very Important Person
// (free shipping on every order + access to the affiliate referral dashboard).
const ROLE_OPTIONS = ['customer', 'affiliate', 'ambassador', 'vip', 'admin'];
const ROLE_LABELS = {
  customer:   'Customer',
  affiliate:  'Affiliate',
  ambassador: 'Ambassador',
  vip:        'Very Important Person',
  admin:      'Admin',
};
const roleLabel = (r) => ROLE_LABELS[r] || capitalize(r);

const SORT_OPTIONS = [
  { value: 'createdAt',              label: 'Joined date' },
  { value: 'lastLoginAt',            label: 'Last login' },
  { value: 'totalSpentCents',        label: 'Total spent' },
  { value: 'ordersCount',            label: 'Orders count' },
  { value: 'commissionsEarnedCents', label: 'Commissions earned' },
  { value: 'downlineCount',          label: 'Downline size' },
  { value: 'walletBalanceCents',     label: 'Referral cash' },
  { value: 'email',                  label: 'Email (A→Z)' },
  { value: 'firstName',              label: 'First name' },
  { value: 'lastName',               label: 'Last name' },
  { value: 'role',                   label: 'Role' },
];

function UsersList() {
  const { fmt } = useCurrency({ usd: true });
  const [users, setUsers]           = useState([]);
  const [pagination, setPagination] = useState({ pages: 1, total: 0 });
  const [loading, setLoading]       = useState(false);
  const [loadError, setLoadError]   = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const [filters, setFilters] = useState({
    search: '', role: '', status: '', tier: '', verified: '',
    sortBy: 'createdAt', sortDir: 'desc', page: 1, limit: 25,
  });

  // Debounce search input
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => ({ ...f, search: searchInput.trim(), page: 1 }));
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = () => {
    setLoading(true);
    setLoadError(false);
    const params = {};
    for (const [k, v] of Object.entries(filters)) {
      if (v !== '' && v !== null && v !== undefined) params[k] = v;
    }
    api.get('/admin/users', { params })
      .then(res => { setUsers(res.data.data || []); setPagination(res.data.pagination || { pages: 1, total: 0 }); })
      .catch(() => { toast.error('Failed to load users'); setLoadError(true); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [filters]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v, page: 1 }));

  const handleSortClick = (column) => {
    setFilters(f => ({
      ...f,
      sortBy: column,
      sortDir: f.sortBy === column && f.sortDir === 'desc' ? 'asc' : 'desc',
      page: 1,
    }));
  };

  const sortArrow = (column) =>
    filters.sortBy !== column ? '' : (filters.sortDir === 'asc' ? ' ▲' : ' ▼');

  const updateUserInline = async (id, patch) => {
    try {
      const res = await api.patch('/admin/users/' + id, patch);
      const updated = res.data.user;
      setUsers(prev => prev.map(u => u.id === id ? { ...u, ...updated } : u));
      toast.success('User updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update user');
    }
  };

  const toggleSuspend = (u, e) => {
    e.stopPropagation();
    const next = !u.isActive;
    const verb = next ? 'reactivate' : 'suspend';
    if (!window.confirm(`${capitalize(verb)} ${u.email}? ` +
      (next ? '' : 'They will be unable to log in or purchase.'))) return;
    updateUserInline(u.id, { isActive: next });
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      search: '', role: '', status: '', tier: '', verified: '',
      sortBy: 'createdAt', sortDir: 'desc', page: 1, limit: 25,
    });
  };

  const activeFilterCount =
    (filters.role ? 1 : 0) + (filters.status ? 1 : 0) +
    (filters.tier ? 1 : 0) + (filters.verified ? 1 : 0) +
    (filters.search ? 1 : 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-sm text-gray-500 mt-1">
            {pagination.total.toLocaleString()} total · god-tier control
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-xl p-4 mb-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          <div className="lg:col-span-2">
            <label className="block text-xs font-medium text-gray-500 mb-1">Search</label>
            <input
              type="text"
              placeholder="Name, email, or user ID…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-tea-400"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role</label>
            <select value={filters.role} onChange={e => setFilter('role', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">All roles</option>
              {ROLE_OPTIONS.map(r => (
                <option key={r} value={r}>{roleLabel(r)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
            <select value={filters.status} onChange={e => setFilter('status', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Tier</label>
            <select value={filters.tier} onChange={e => setFilter('tier', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">All tiers</option>
              <option value="standard">Standard</option>
              <option value="platinum">Platinum</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Verified</label>
            <select value={filters.verified} onChange={e => setFilter('verified', e.target.value)}
              className="w-full text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white">
              <option value="">Any</option>
              <option value="true">Verified</option>
              <option value="false">Unverified</option>
            </select>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-gray-500 font-medium">Sort by</label>
            <select value={filters.sortBy} onChange={e => setFilter('sortBy', e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <select value={filters.sortDir} onChange={e => setFilter('sortDir', e.target.value)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              <option value="desc">↓ Desc</option>
              <option value="asc">↑ Asc</option>
            </select>
            <label className="text-xs text-gray-500 font-medium ml-2">Per page</label>
            <select value={filters.limit} onChange={e => setFilter('limit', parseInt(e.target.value))}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
              {[10, 25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters}
              className="text-xs text-tea-600 hover:text-tea-700 font-medium">
              Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <Th onClick={() => handleSortClick('firstName')}>Name{sortArrow('firstName')}</Th>
              <Th onClick={() => handleSortClick('email')}>Email{sortArrow('email')}</Th>
              <Th onClick={() => handleSortClick('role')}>Role{sortArrow('role')}</Th>
              <Th onClick={() => handleSortClick('tier')}>Tier{sortArrow('tier')}</Th>
              <Th>Status</Th>
              <Th onClick={() => handleSortClick('ordersCount')}    align="right">Orders{sortArrow('ordersCount')}</Th>
              <Th onClick={() => handleSortClick('totalSpentCents')} align="right">Spent{sortArrow('totalSpentCents')}</Th>
              <Th onClick={() => handleSortClick('commissionsEarnedCents')} align="right">Earned{sortArrow('commissionsEarnedCents')}</Th>
              <Th onClick={() => handleSortClick('downlineCount')}  align="right">Downline{sortArrow('downlineCount')}</Th>
              <Th onClick={() => handleSortClick('walletBalanceCents')} align="right">Referral cash{sortArrow('walletBalanceCents')}</Th>
              <Th onClick={() => handleSortClick('createdAt')}>Joined{sortArrow('createdAt')}</Th>
              <Th onClick={() => handleSortClick('lastLoginAt')}>Last login{sortArrow('lastLoginAt')}</Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-gray-400">Loading…</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={13} className="px-4 py-10 text-center">
                <Icon name="warning" className="w-7 h-7 mx-auto mb-2 text-amber-400" />
                <div className="text-gray-600 font-medium mb-3">Failed to load users</div>
                <button onClick={load} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">Retry</button>
              </td></tr>
            )}
            {!loading && !loadError && users.length === 0 && (
              <tr><td colSpan={13} className="px-4 py-10 text-center text-gray-400">No users match your filters.</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id}
                onClick={() => setSelectedId(u.id)}
                className="border-b last:border-0 hover:bg-tea-50 cursor-pointer">
                <td className="px-3 py-2.5">
                  <div className="font-medium text-gray-900">{u.firstName} {u.lastName}</div>
                  <div className="text-xs text-gray-400 font-mono">{u.id.slice(0, 8)}</div>
                </td>
                <td className="px-3 py-2.5 text-gray-600">
                  <div className="flex items-center gap-1">
                    <span className="truncate max-w-[14rem]" title={u.email}>{u.email}</span>
                    {!u.isEmailVerified && (
                      <Icon name="warning" title="Email not verified" className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    )}
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' + (ROLE_BADGE[u.role] || ROLE_BADGE.customer)}>
                    {roleLabel(u.role)}
                  </span>
                  {u.role === 'ambassador' && (
                    <span title="Earns 2× on every commission line" className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                      2×
                    </span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  {u.tier === 'platinum' ? (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600"><Icon name="sparkles" className="w-3.5 h-3.5" /> Platinum</span>
                  ) : (
                    <span className="text-xs text-gray-400">Standard</span>
                  )}
                </td>
                <td className="px-3 py-2.5">
                  <span className={u.isActive ? 'badge-green' : 'badge-red'}>
                    {u.isActive ? 'Active' : 'Suspended'}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{u.ordersCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums font-medium">
                  {fmt(u.totalSpentCents)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums text-tea-700">
                  {fmt(u.commissionsEarnedCents)}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">{u.downlineCount}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {fmt(u.walletBalanceCents)}
                </td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                <td className="px-3 py-2.5 text-gray-500 text-xs">
                  {u.lastLoginAt ? formatDate(u.lastLoginAt) : <span className="text-gray-300">—</span>}
                </td>
                <td className="px-3 py-2.5" onClick={e => e.stopPropagation()}>
                  <div className="flex gap-2 items-center">
                    <button onClick={() => setSelectedId(u.id)}
                      className="text-xs text-tea-600 hover:text-tea-700 font-medium">Edit</button>
                    <button onClick={(e) => toggleSuspend(u, e)}
                      className={'text-xs font-medium ' +
                        (u.isActive ? 'text-red-600 hover:text-red-700' : 'text-green-600 hover:text-green-700')}>
                      {u.isActive ? 'Suspend' : 'Reactivate'}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
          <div className="text-xs text-gray-500">
            Page {filters.page} of {pagination.pages} · {pagination.total.toLocaleString()} users
          </div>
          <div className="flex gap-1">
            <button onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))}
              disabled={filters.page === 1}
              className="px-3 py-1 rounded border text-sm disabled:opacity-30 hover:border-tea-400">Prev</button>
            <button onClick={() => setFilters(f => ({ ...f, page: Math.min(pagination.pages, f.page + 1) }))}
              disabled={filters.page >= pagination.pages}
              className="px-3 py-1 rounded border text-sm disabled:opacity-30 hover:border-tea-400">Next</button>
          </div>
        </div>
      )}

      {selectedId && (
        <UserDetailDrawer
          userId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={(updated) => {
            setUsers(prev => prev.map(u => u.id === updated.id ? { ...u, ...updated } : u));
          }}
        />
      )}
    </div>
  );
}

function Th({ children, onClick, align }) {
  const cls = 'px-3 py-3 font-semibold text-gray-600 whitespace-nowrap ' +
    (align === 'right' ? 'text-right ' : 'text-left ') +
    (onClick ? 'cursor-pointer hover:text-tea-700 select-none' : '');
  return <th className={cls} onClick={onClick}>{children}</th>;
}

// ─── User detail drawer (slide-over edit-anything panel) ─────────────────────
const DRAWER_TABS = [
  { id: 'profile',       label: 'Profile' },
  { id: 'orders',        label: 'Orders' },
  { id: 'commissions',   label: 'Commissions' },
  { id: 'network',       label: 'Network' },
];

function UserDetailDrawer({ userId, onClose, onUpdated }) {
  const { fmt } = useCurrency({ usd: true });
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [tab, setTab]         = useState('profile');
  const [form, setForm]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/users/' + userId)
      .then(res => {
        if (cancelled) return;
        setData(res.data);
        setForm({
          firstName:          res.data.user.firstName || '',
          lastName:           res.data.user.lastName || '',
          email:              res.data.user.email || '',
          role:               res.data.user.role,
          tier:               res.data.user.tier,
          isActive:           res.data.user.isActive,
          isEmailVerified:    res.data.user.isEmailVerified,
          walletBalanceCents: res.data.user.walletBalanceCents,
          dateOfBirth:        res.data.user.dateOfBirth || '',
          bonusRewardsUntil:  res.data.user.bonusRewardsUntil
            ? new Date(res.data.user.bonusRewardsUntil).toISOString().slice(0, 16)
            : '',
          referredById:       res.data.user.referredById || '',
        });
      })
      .catch(() => toast.error('Failed to load user'))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userId]);

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        role: form.role,
        tier: form.tier,
        isActive: form.isActive,
        isEmailVerified: form.isEmailVerified,
        walletBalanceCents: parseInt(form.walletBalanceCents, 10),
        dateOfBirth: form.dateOfBirth || null,
        bonusRewardsUntil: form.bonusRewardsUntil
          ? new Date(form.bonusRewardsUntil).toISOString()
          : null,
        referredById: form.referredById || null,
      };
      const res = await api.patch('/admin/users/' + userId, payload);
      toast.success('User saved');
      setData(d => ({ ...d, user: { ...d.user, ...res.data.user } }));
      onUpdated?.({ id: userId, ...res.data.user });
    } catch (err) {
      toast.error(err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const adjustWallet = (deltaDollars) => {
    const cur = parseInt(form.walletBalanceCents, 10) || 0;
    const next = Math.max(0, cur + Math.round(deltaDollars * 100));
    setField('walletBalanceCents', next);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white w-full max-w-3xl h-full shadow-2xl flex flex-col">
        {loading && (
          <div className="flex items-center justify-center flex-1 text-gray-400">Loading…</div>
        )}
        {!loading && data && (
          <>
            {/* Header */}
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold truncate">
                    {data.user.firstName} {data.user.lastName}
                  </h2>
                  <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' + (ROLE_BADGE[data.user.role] || ROLE_BADGE.customer)}>
                    {roleLabel(data.user.role)}
                  </span>
                  {data.user.role === 'ambassador' && (
                    <span title="Earns 2× on every commission line" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                      2× commissions
                    </span>
                  )}
                  {data.user.tier === 'platinum' && (
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-600"><Icon name="sparkles" className="w-3.5 h-3.5" /> Platinum</span>
                  )}
                  {!data.user.isActive && <span className="badge-red">Suspended</span>}
                </div>
                <p className="text-xs text-gray-500 truncate">{data.user.email} · <span className="font-mono">{data.user.id}</span></p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none px-2">&times;</button>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 px-6 py-3 border-b bg-gray-50 text-center text-xs">
              <Stat label="Orders"        value={data.stats.ordersCount} />
              <Stat label="Spent"         value={fmt(data.stats.totalSpentCents)} />
              <Stat label="Commissions"   value={fmt(data.stats.commissionsEarnedCents)} />
              <Stat label="Downline"      value={data.stats.downlineCount} />
              <Stat label="Referral cash" value={fmt(data.user.walletBalanceCents)} highlight />
            </div>

            {/* Tabs */}
            <div className="border-b px-6 flex gap-1">
              {DRAWER_TABS.map(t => (
                <button key={t.id} onClick={() => setTab(t.id)}
                  className={'px-3 py-2 text-sm font-medium border-b-2 -mb-px ' +
                    (tab === t.id ? 'border-tea-600 text-tea-700' : 'border-transparent text-gray-500 hover:text-gray-700')}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {tab === 'profile' && form && (
                <ProfileTab form={form} setField={setField} user={data.user} adjustWallet={adjustWallet} />
              )}
              {tab === 'orders'        && <OrdersTab        orders={data.orders} />}
              {tab === 'commissions'   && <CommissionsTab   commissions={data.commissions} />}
              {tab === 'network'       && <NetworkTab       upline={data.user.upline} downline={data.downline} codes={data.referralCodes} downlineBreakdown={data.downlineBreakdown} />}
            </div>

            {/* Footer actions (only on profile tab) */}
            {tab === 'profile' && (
              <div className="border-t px-6 py-3 flex items-center justify-between bg-gray-50">
                <div className="text-xs text-gray-500">
                  Last updated {formatDate(data.user.updatedAt)}
                </div>
                <div className="flex gap-2">
                  <button onClick={onClose}
                    className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-white">
                    Cancel
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-4 py-2 text-sm rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={'rounded p-2 ' + (highlight ? 'bg-tea-100' : 'bg-white border')}>
      <div className={'text-sm font-bold ' + (highlight ? 'text-tea-700' : 'text-gray-800')}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

function Toggle({ checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={'w-11 h-6 rounded-full transition-colors relative ' + (checked ? 'bg-tea-600' : 'bg-gray-300')}>
      <span className={'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform shadow ' +
        (checked ? 'translate-x-5' : 'translate-x-0')} />
    </button>
  );
}

function ProfileTab({ form, setField, user, adjustWallet }) {
  const walletCents = parseInt(form.walletBalanceCents, 10) || 0;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First name">
          <input value={form.firstName} onChange={e => setField('firstName', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Last name">
          <input value={form.lastName} onChange={e => setField('lastName', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Email" hint="Changing this changes their login email.">
          <input type="email" value={form.email} onChange={e => setField('email', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Date of birth (21+ check)">
          <input type="date" value={form.dateOfBirth || ''} onChange={e => setField('dateOfBirth', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>
        <Field label="Role" hint={
          form.role === 'ambassador'
            ? 'Brand Ambassador — earns 2× on every commission line (all levels).'
            : form.role === 'vip'
              ? 'Very Important Person — free shipping on every order + the affiliate referral dashboard.'
              : 'Ambassador grants a 2× commission rate; VIP grants free shipping + the referral dashboard.'}>
          <select value={form.role} onChange={e => setField('role', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
            {ROLE_OPTIONS.map(r => (
              <option key={r} value={r}>{roleLabel(r)}</option>
            ))}
          </select>
        </Field>
        <Field label="Tier" hint="Platinum-tier affiliates earn Level-3 (2%) commissions.">
          <select value={form.tier} onChange={e => setField('tier', e.target.value)}
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white">
            <option value="standard">Standard</option>
            <option value="platinum">Platinum</option>
          </select>
        </Field>
        <Field label="Referred by (upline user ID)" hint={
          user.upline ? `Currently: ${user.upline.firstName} ${user.upline.lastName} (${user.upline.email})` : 'No upline'
        }>
          <input value={form.referredById} onChange={e => setField('referredById', e.target.value)}
            placeholder="UUID or leave blank"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 font-mono text-xs" />
        </Field>
        <Field label="Bonus rewards (2× commissions) until">
          <div className="flex gap-2 items-center">
            <input type="datetime-local" value={form.bonusRewardsUntil}
              onChange={e => setField('bonusRewardsUntil', e.target.value)}
              className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2" />
            <button
              type="button"
              onClick={() => {
                const d = new Date();
                d.setDate(d.getDate() + 28);
                setField('bonusRewardsUntil', d.toISOString().slice(0, 16));
              }}
              className="shrink-0 text-xs font-semibold px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 whitespace-nowrap"
            >
              +28 days
            </button>
            {form.bonusRewardsUntil && (
              <button
                type="button"
                onClick={() => setField('bonusRewardsUntil', '')}
                className="shrink-0 text-xs font-medium px-3 py-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 whitespace-nowrap"
              >
                Clear
              </button>
            )}
          </div>
          {form.bonusRewardsUntil && (() => {
            const expires = new Date(form.bonusRewardsUntil);
            const now = new Date();
            const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
            return (
              <p className={'text-[11px] mt-1 ' + (daysLeft > 0 ? 'text-amber-600' : 'text-red-500')}>
                {daysLeft > 0 ? `Active — expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}` : 'Bonus period has expired'}
              </p>
            );
          })()}
        </Field>
      </div>

      {/* Toggles */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
          <div>
            <div className="font-medium text-gray-800">Account active</div>
            <div className="text-xs text-gray-500">Suspended users cannot log in or purchase.</div>
          </div>
          <Toggle checked={form.isActive} onChange={v => setField('isActive', v)} />
        </label>
        <label className="flex items-center justify-between gap-3 text-sm cursor-pointer">
          <div>
            <div className="font-medium text-gray-800">Email verified</div>
            <div className="text-xs text-gray-500">Bypass email-verification gate manually.</div>
          </div>
          <Toggle checked={form.isEmailVerified} onChange={v => setField('isEmailVerified', v)} />
        </label>
      </div>

      {/* Referral cash adjuster */}
      <div className="bg-tea-50 border border-tea-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="font-medium text-tea-900 text-sm">Referral cash</div>
            <div className="text-xs text-gray-600">
              Customer-visible balance — applies at checkout against items only (shipping is still charged).
              <span className="block text-[11px] text-gray-500 mt-0.5">
                Stored in <strong>USD</strong>; all adjust controls below are USD dollars.
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-tea-700 tabular-nums">
              ${(walletCents / 100).toFixed(2)} <span className="text-xs font-medium text-tea-700/70">USD</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <span className="text-xs text-gray-500">Quick adjust (USD):</span>
          {[-50, -10, -5, 5, 10, 50].map(amt => (
            <button key={amt} type="button" onClick={() => adjustWallet(amt)}
              className={'text-xs font-medium px-2 py-1 rounded border ' +
                (amt > 0 ? 'border-tea-300 text-tea-700 hover:bg-tea-100' : 'border-red-200 text-red-600 hover:bg-red-50')}>
              {amt > 0 ? '+' : ''}{amt}$
            </button>
          ))}
          <div className="flex items-center gap-1 ml-2">
            <span className="text-xs text-gray-500">Set USD $</span>
            <input type="number" min="0" step="0.01"
              value={walletCents / 100}
              onChange={e => setField('walletBalanceCents', Math.max(0, Math.round(parseFloat(e.target.value || '0') * 100)))}
              className="w-24 text-sm border border-gray-300 rounded px-2 py-1" />
          </div>
        </div>
      </div>

      <div className="text-xs text-gray-400">
        Joined {formatDate(user.createdAt)} · Last login {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'never'}
      </div>
    </div>
  );
}

function OrdersTab({ orders }) {
  const { fmt } = useCurrency({ usd: true });
  if (!orders?.length) return <p className="text-sm text-gray-500">No orders yet.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="border-b text-xs text-gray-500">
        <tr>
          <th className="text-left py-2">ID</th><th className="text-left">Status</th>
          <th className="text-left">Payment</th><th className="text-right">Total</th>
          <th className="text-left">Tracking</th><th className="text-left">Date</th>
        </tr>
      </thead>
      <tbody>
        {orders.map(o => (
          <tr key={o.id} className="border-b last:border-0">
            <td className="py-2 font-mono text-xs text-gray-500">{o.id.slice(0, 8)}</td>
            <td><span className={statusBadge(o.status)}>{capitalize(o.status)}</span></td>
            <td className="text-xs text-gray-600">{capitalize(o.paymentMethod || 'card')}</td>
            <td className="text-right tabular-nums font-medium">{fmt(o.totalInCents)}</td>
            <td className="text-xs font-mono text-gray-600">{o.trackingNumber || '—'}</td>
            <td className="text-xs text-gray-500">{formatDate(o.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CommissionsTab({ commissions }) {
  const { fmt } = useCurrency({ usd: true });
  if (!commissions?.length) return <p className="text-sm text-gray-500">No commissions earned.</p>;
  return (
    <table className="w-full text-sm">
      <thead className="border-b text-xs text-gray-500">
        <tr>
          <th className="text-left py-2">Level</th><th className="text-left">Status</th>
          <th className="text-right">Rate</th><th className="text-right">Amount</th>
          <th className="text-left">Date</th>
        </tr>
      </thead>
      <tbody>
        {commissions.map(c => (
          <tr key={c.id} className="border-b last:border-0">
            <td className="py-2">L{c.level}</td>
            <td><span className={statusBadge(c.status)}>{capitalize(c.status)}</span></td>
            <td className="text-right">{(parseFloat(c.rate) * 100).toFixed(2)}%</td>
            <td className="text-right tabular-nums font-medium">{fmt(c.amountInCents)}</td>
            <td className="text-xs text-gray-500">{formatDate(c.createdAt)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NetworkTab({ upline, downline, codes, downlineBreakdown }) {
  const [qrCode, setQrCode] = useState(null);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Upline</h4>
        {upline ? (
          <div className="bg-white border rounded p-3 text-sm">
            <div className="font-medium">{upline.firstName} {upline.lastName}</div>
            <div className="text-xs text-gray-500">{upline.email}</div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">No upline (root account or organic signup).</p>
        )}
      </div>


      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Direct downline ({downline?.length || 0})
        </h4>
        {downline?.length ? (
          <div className="border rounded divide-y">
            {downline.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 text-sm">
                <div>
                  <div className="font-medium">{d.firstName} {d.lastName}</div>
                  <div className="text-xs text-gray-500">{d.email} · {roleLabel(d.role)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-400">{formatDate(d.createdAt)}</div>
                  {!d.isActive && <span className="badge-red text-[10px]">Suspended</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-400">No direct referrals.</p>
        )}
      </div>

      <div>
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          Referral codes ({codes?.length || 0})
        </h4>
        {codes?.length ? (
          <table className="w-full text-sm">
            <thead className="border-b text-xs text-gray-500">
              <tr>
                <th className="text-left py-2">Code</th><th className="text-left">Label</th>
                <th className="text-right">Visits</th><th className="text-right">Conversions</th>
                <th className="text-right">QR</th>
              </tr>
            </thead>
            <tbody>
              {codes.map(c => (
                <tr key={c.id} className="border-b last:border-0">
                  <td className="py-2 font-mono font-bold text-tea-700">{c.code}</td>
                  <td className="text-gray-600">{c.label}</td>
                  <td className="text-right">{c.visits}</td>
                  <td className="text-right">{c.conversions}</td>
                  <td className="text-right">
                    <button onClick={() => setQrCode(c)}
                      className="text-xs text-tea-600 hover:underline font-medium">
                      QR Code
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-sm text-gray-400">No referral codes.</p>
        )}
      </div>

      {qrCode && (
        <ReferralQRModal
          code={qrCode.code}
          label={qrCode.label}
          onClose={() => setQrCode(null)}
        />
      )}
    </div>
  );
}

// ─── Products management ─────────────────────────────────────────────────────
const slugify = (s) =>
  s.toString().toLowerCase().trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const EMPTY_VARIANT = { name: '', sku: '', priceDollars: '', comparePriceDollars: '', costDollars: '', bulkPriceDollars: '', bulkMinQty: 1, inventory: 0, isActive: true };

const EMPTY_PRODUCT = {
  name: '',
  slug: '',
  description: '',
  imageUrl: '',
  seoTitle: '',
  seoDescription: '',
  commissionOverrideL1: '',
  commissionOverrideL2: '',
  commissionOverrideL3: '',
  isActive: true,
  bulkEligible: false,
  category: '',
  featuredOnHome: false,
  variants: [{ ...EMPTY_VARIANT }],
};

function ProductForm({ initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const { categories } = useCategories();
  const [form, setForm] = useState(() => {
    if (!initial) return { ...EMPTY_PRODUCT };
    return {
      ...EMPTY_PRODUCT,
      ...initial,
      commissionOverrideL1: initial.commissionOverrideL1 ?? '',
      commissionOverrideL2: initial.commissionOverrideL2 ?? '',
      commissionOverrideL3: initial.commissionOverrideL3 ?? '',
      variants: (initial.variants || []).map(v => ({
        id: v.id,
        name: v.name,
        sku: v.sku,
        priceDollars: (v.priceInCents / 100).toFixed(2),
        comparePriceDollars: v.comparePriceInCents != null ? (v.comparePriceInCents / 100).toFixed(2) : '',
        costDollars: v.costInCents != null ? (v.costInCents / 100).toFixed(2) : '',
        bulkPriceDollars: v.bulkPriceInCents != null ? (v.bulkPriceInCents / 100).toFixed(2) : '',
        bulkMinQty: v.bulkMinQty || 1,
        inventory: v.inventory,
        isActive: v.isActive,
      })),
    };
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImageFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/uploads', fd);
      setField('imageUrl', res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const setVariant = (idx, k, v) =>
    setForm(f => ({ ...f, variants: f.variants.map((va, i) => i === idx ? { ...va, [k]: v } : va) }));
  const addVariant = () => setForm(f => ({ ...f, variants: [...f.variants, { ...EMPTY_VARIANT }] }));
  const removeVariant = (idx) => setForm(f => ({ ...f, variants: f.variants.filter((_, i) => i !== idx) }));

  // Auto-fill slug on name change if user hasn't touched slug yet (create only)
  const onNameChange = (val) => {
    setForm(f => ({ ...f, name: val, slug: (!isEdit && (!f.slug || f.slug === slugify(f.name))) ? slugify(val) : f.slug }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const productPayload = {
        name: form.name,
        slug: form.slug,
        description: form.description || undefined,
        imageUrl: form.imageUrl || undefined,
        seoTitle: form.seoTitle || undefined,
        seoDescription: form.seoDescription || undefined,
        isActive: form.isActive,
        bulkEligible: !!form.bulkEligible,
        category: form.category || null,
        featuredOnHome: !!form.featuredOnHome,
        commissionOverrideL1: form.commissionOverrideL1 === '' ? null : parseFloat(form.commissionOverrideL1),
        commissionOverrideL2: form.commissionOverrideL2 === '' ? null : parseFloat(form.commissionOverrideL2),
        commissionOverrideL3: form.commissionOverrideL3 === '' ? null : parseFloat(form.commissionOverrideL3),
      };

      const variants = form.variants.map(v => ({
        ...(v.id ? { id: v.id } : {}),
        name: v.name,
        sku: v.sku || (form.slug.replace(/-/g, '') + v.name.replace(/[^a-zA-Z0-9]/g, '')).toUpperCase(),
        priceInCents: Math.round(parseFloat(v.priceDollars) * 100),
        comparePriceInCents: v.comparePriceDollars ? Math.round(parseFloat(v.comparePriceDollars) * 100) : null,
        costInCents: v.costDollars !== '' && v.costDollars != null ? Math.round(parseFloat(v.costDollars) * 100) : null,
        bulkPriceInCents: form.bulkEligible && v.bulkPriceDollars !== '' && v.bulkPriceDollars != null
          ? Math.round(parseFloat(v.bulkPriceDollars) * 100) : null,
        bulkMinQty: parseInt(v.bulkMinQty, 10) > 0 ? parseInt(v.bulkMinQty, 10) : 1,
        inventory: parseInt(v.inventory, 10),
        isActive: v.isActive,
      }));

      if (isEdit) {
        await api.put('/products/' + initial.id, { ...productPayload, variants });
        toast.success('Product updated');
      } else {
        await api.post('/products', { ...productPayload, variants });
        toast.success('Product created');
      }
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.fields?.[0]?.message || err.response?.data?.error || 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl my-8">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">{isEdit ? 'Edit Product' : 'Add Product'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">
          {/* Basic info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input required value={form.name} onChange={e => onNameChange(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
              <input required value={form.slug} onChange={e => setField('slug', e.target.value)}
                pattern="[a-z0-9-]+"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
              <p className="text-xs text-gray-400 mt-1">lowercase, hyphens only</p>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea rows={3} value={form.description} onChange={e => setField('description', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
          </div>

          {/* Product image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Product Image</label>
            <div className="flex items-start gap-4">
              {/* Preview */}
              <div className="w-24 h-24 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                {form.imageUrl ? (
                  <img src={form.imageUrl}
                    alt="product" className="w-full h-full object-cover" />
                ) : (
                  <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden"
                  onChange={handleImageFile} />
                <button type="button" disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 font-medium">
                  {uploading ? 'Uploading…' : 'Upload image'}
                </button>
                {form.imageUrl && (
                  <button type="button" onClick={() => setField('imageUrl', '')}
                    className="block text-xs text-red-500 hover:text-red-700">Remove image</button>
                )}
                <p className="text-xs text-gray-400">JPEG, PNG, WebP or GIF · max 8 MB</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Title</label>
              <input value={form.seoTitle} onChange={e => setField('seoTitle', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SEO Description</label>
              <input value={form.seoDescription} onChange={e => setField('seoDescription', e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Catalogue category + homepage carousel */}
          <div className="rounded-lg border border-pink-200 bg-pink-50/60 p-4 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-800 mb-1">Catalogue category</label>
              <p className="text-[11px] text-gray-500 mb-2">
                Which collection this product belongs to. Drives the homepage carousels.
                {' '}Manage the list under <Link to="/admin/categories" className="text-pink-600 hover:underline font-medium">Categories</Link>.
              </p>
              <div className="flex flex-wrap gap-2">
                {/* "None" pill */}
                <button type="button" onClick={() => setField('category', '')}
                  className={'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ' +
                    (!form.category
                      ? 'bg-gray-800 text-white border-gray-800'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400')}>
                  Uncategorised
                </button>
                {categories.map(c => (
                  <button key={c.value} type="button" onClick={() => setField('category', c.value)}
                    className={'px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ' +
                      (form.category === c.value
                        ? 'bg-pink-600 text-white border-pink-600 shadow-sm'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-pink-400')}>
                    <CategoryIcon category={c.icon || c.value} className="inline-block w-4 h-4 mr-1.5 -mt-0.5" />{c.label}
                  </button>
                ))}
              </div>
            </div>

            <label className={'flex items-start gap-2 text-sm font-medium cursor-pointer rounded-md p-2 -m-2 transition-colors ' +
              (form.featuredOnHome ? 'text-pink-800' : 'text-gray-800') +
              (form.featuredOnHome && !form.category ? '' : '')}>
              <input type="checkbox" className="mt-0.5" checked={!!form.featuredOnHome}
                onChange={e => setField('featuredOnHome', e.target.checked)} />
              <span>
                <span className="inline-flex items-center gap-1.5"><Icon name="sparkles" className="w-4 h-4 text-tea-500" /> Feature on the homepage carousel</span>
                <span className="block text-[11px] font-normal text-gray-500 mt-0.5">
                  When on, this product scrolls in its category's wheel on the home page.
                  {form.featuredOnHome && !form.category && (
                    <span className="inline-flex items-center gap-1 text-amber-700 font-medium mt-0.5">
                      <Icon name="warning" className="w-3.5 h-3.5" /> Pick a category above so it has a carousel to appear in.
                    </span>
                  )}
                </span>
              </span>
            </label>
          </div>

          {/* Commission overrides */}
          <div>
            <div className="text-sm font-medium text-gray-700 mb-2">Commission overrides (decimal, e.g. 0.20 for 20%)</div>
            <div className="grid grid-cols-3 gap-3">
              {['L1', 'L2', 'L3'].map(level => (
                <div key={level}>
                  <label className="block text-xs text-gray-500 mb-1">Level {level.slice(1)}</label>
                  <input type="number" step="0.0001" min="0" max="1"
                    value={form['commissionOverride' + level]}
                    onChange={e => setField('commissionOverride' + level, e.target.value)}
                    placeholder="—"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
              ))}
            </div>
          </div>

          {/* Bulk Vault eligibility */}
          <div className={'rounded-lg border p-4 transition-colors ' + (form.bulkEligible ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50')}>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-800 cursor-pointer">
              <input type="checkbox" checked={!!form.bulkEligible}
                onChange={e => setField('bulkEligible', e.target.checked)} />
              <span>Bulk eligible — sell this product wholesale in the Bulk Vault</span>
            </label>
            <p className="text-[11px] text-gray-500 mt-1 ml-6">
              When on, set a <strong>wholesale price</strong> and <strong>minimum quantity</strong> per
              variant below. The wholesale price is charged automatically once a customer orders at or
              above the minimum.
            </p>
          </div>

          {/* Variants */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-medium text-gray-700">Variants *</div>
                <div className="text-[11px] text-gray-500">
                  Prices are stored and charged in <strong>USD</strong>.
                </div>
              </div>
              <button type="button" onClick={addVariant}
                className="text-xs text-tea-600 hover:text-tea-700 font-medium">+ Add variant</button>
            </div>
            <div className="space-y-3">
              {form.variants.map((v, idx) => {
                const priceNum = parseFloat(v.priceDollars);
                const costNum  = parseFloat(v.costDollars);
                const hasMargin = !isNaN(priceNum) && !isNaN(costNum) && priceNum > 0;
                const profit   = hasMargin ? (priceNum - costNum) : null;
                const marginPct = hasMargin ? Math.round((profit / priceNum) * 100) : null;
                const marginColor = marginPct == null ? 'text-gray-400'
                  : marginPct >= 50 ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                  : marginPct >= 25 ? 'text-amber-700 bg-amber-50 border-amber-200'
                  : 'text-red-700 bg-red-50 border-red-200';
                return (
                <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-gray-50">
                  <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
                    <div className="col-span-2 md:col-span-2">
                      <label className="block text-xs text-gray-500 mb-1">Name</label>
                      <input required value={v.name} onChange={e => setVariant(idx, 'name', e.target.value)}
                        placeholder="30-bag Mint"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Price (USD)</label>
                      <input required type="number" step="0.01" min="0.01"
                        value={v.priceDollars} onChange={e => setVariant(idx, 'priceDollars', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1" title="Your unit cost — used in Logistics profitability">Cost (USD)</label>
                      <input type="number" step="0.01" min="0"
                        value={v.costDollars} onChange={e => setVariant(idx, 'costDollars', e.target.value)}
                        placeholder="—"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Compare (USD)</label>
                      <input type="number" step="0.01" min="0"
                        value={v.comparePriceDollars} onChange={e => setVariant(idx, 'comparePriceDollars', e.target.value)}
                        placeholder="—"
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Stock</label>
                      <input required type="number" min="0"
                        value={v.inventory} onChange={e => setVariant(idx, 'inventory', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    </div>
                  </div>

                  {/* Bulk / wholesale pricing — only when the product is bulk eligible */}
                  {form.bulkEligible && (() => {
                    const bulkNum = parseFloat(v.bulkPriceDollars);
                    const retailNum = parseFloat(v.priceDollars);
                    const hasBulk = !isNaN(bulkNum) && bulkNum > 0;
                    const savePct = hasBulk && !isNaN(retailNum) && retailNum > 0
                      ? Math.round((1 - bulkNum / retailNum) * 100) : null;
                    return (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-6 gap-2 rounded-md border border-amber-200 bg-amber-50/60 p-2">
                        <div className="md:col-span-2">
                          <label className="block text-xs text-amber-800 font-medium mb-1">Wholesale price (USD)</label>
                          <input type="number" step="0.01" min="0"
                            value={v.bulkPriceDollars} onChange={e => setVariant(idx, 'bulkPriceDollars', e.target.value)}
                            placeholder="per-unit bulk price"
                            className="w-full border border-amber-300 rounded px-2 py-1 text-sm" />
                        </div>
                        <div>
                          <label className="block text-xs text-amber-800 font-medium mb-1">Min qty</label>
                          <input type="number" min="1"
                            value={v.bulkMinQty} onChange={e => setVariant(idx, 'bulkMinQty', e.target.value)}
                            className="w-full border border-amber-300 rounded px-2 py-1 text-sm" />
                        </div>
                        <div className="md:col-span-3 flex items-end">
                          {savePct != null && savePct > 0 ? (
                            <span className="text-xs font-semibold text-amber-800 bg-white border border-amber-200 rounded-full px-2 py-0.5">
                              {savePct}% off retail at {v.bulkMinQty || 1}+ units
                            </span>
                          ) : (
                            <span className="text-[11px] text-gray-400">Leave blank for no bulk deal on this size.</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
                      <input type="checkbox" checked={v.isActive}
                        onChange={e => setVariant(idx, 'isActive', e.target.checked)} />
                      Active
                    </label>
                    {hasMargin && (
                      <div className={'text-xs font-semibold px-2 py-0.5 rounded-full border ' + marginColor}>
                        Profit ${profit.toFixed(2)} · {marginPct}% margin
                      </div>
                    )}
                    {form.variants.length > 1 && (
                      <button type="button" onClick={() => removeVariant(idx)}
                        className="text-xs text-red-600 hover:text-red-700">Remove</button>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>

          {/* Active toggle */}
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.isActive} onChange={e => setField('isActive', e.target.checked)} />
            Active (visible in shop)
          </label>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving}
              className="px-4 py-2 text-sm rounded-lg bg-tea-600 text-white hover:bg-tea-700 disabled:opacity-50">
              {saving ? 'Saving...' : (isEdit ? 'Save changes' : 'Create product')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkPhotoModal({ onClose, onDone }) {
  const [imageUrl, setImageUrl]   = useState('');
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]       = useState(false);
  const fileInputRef              = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/uploads', fd);
      setImageUrl(res.data.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleApply = async () => {
    if (!imageUrl) { toast.error('Please upload a photo first'); return; }
    if (!window.confirm('This will replace the photo on ALL products. Continue?')) return;
    setSaving(true);
    try {
      const res = await api.put('/products/bulk-photo', { imageUrl });
      toast.success(res.data.message);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update photos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-xl font-bold">Bulk Edit Photos</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Upload a photo below and click <strong>Apply to All Products</strong> to set the same image on every product.
          </p>

          <div>
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg py-4 text-sm text-gray-500 hover:border-tea-400 hover:text-tea-600 transition-colors disabled:opacity-50"
            >
              {uploading ? 'Uploading…' : 'Click to upload photo'}
            </button>
          </div>

          {imageUrl && (
            <div className="flex flex-col items-center gap-2">
              <img src={imageUrl} alt="Preview" className="h-40 w-40 object-cover rounded-lg border" />
              <p className="text-xs text-gray-400 break-all">{imageUrl}</p>
            </div>
          )}

          <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700">
            This will overwrite the photo on <strong>all products</strong>.
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border hover:bg-gray-50">Cancel</button>
          <button
            onClick={handleApply}
            disabled={!imageUrl || saving}
            className="px-4 py-2 text-sm rounded-lg bg-tea-600 hover:bg-tea-700 text-white font-medium disabled:opacity-50"
          >
            {saving ? 'Applying…' : 'Apply to All Products'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProductsList() {
  const { label: categoryLabel } = useCategories();
  const [products, setProducts]     = useState([]);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({});
  const [editing, setEditing]       = useState(null); // null | 'new' | product object
  const [loading, setLoading]       = useState(false);
  const [bulkPhoto, setBulkPhoto]   = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/admin/products', { params: { page } })
      .then(res => { setProducts(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load products'))
      .finally(() => setLoading(false));
  };

  useEffect(load, [page]);

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this product? It will be hidden from the shop.')) return;
    try {
      await api.delete('/products/' + id);
      toast.success('Product deactivated');
      load();
    } catch {
      toast.error('Failed to deactivate product');
    }
  };

  const reactivate = async (product) => {
    try {
      await api.put('/products/' + product.id, { isActive: true });
      toast.success('Product reactivated');
      load();
    } catch {
      toast.error('Failed to reactivate product');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Products</h2>
        <div className="flex gap-2">
          <button onClick={() => setBulkPhoto(true)}
            className="border border-gray-300 hover:border-tea-400 hover:text-tea-700 text-gray-600 text-sm font-medium px-4 py-2 rounded-lg">
            Bulk Edit Photos
          </button>
          <button onClick={() => setEditing('new')}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg">
            + Add Product
          </button>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Name', 'Slug', 'Variants', 'Inventory', 'Status', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading...</td></tr>
            )}
            {!loading && products.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No products yet — click "Add Product" to create your first one.</td></tr>
            )}
            {products.map(p => {
              const totalInventory = (p.variants || []).reduce((s, v) => s + (v.inventory || 0), 0);
              const variantCount = (p.variants || []).length;
              return (
                <tr key={p.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center flex-wrap gap-1.5">
                      {p.name}
                      {p.category && (
                        <span title={'Category: ' + categoryLabel(p.category)} className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-pink-100 text-pink-800 border border-pink-200 rounded-full px-1.5 py-0.5">
                          {categoryLabel(p.category)}
                        </span>
                      )}
                      {p.featuredOnHome && (
                        <span title="Featured in the homepage carousel" className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide bg-fuchsia-100 text-fuchsia-800 border border-fuchsia-200 rounded-full px-1.5 py-0.5">
                          <Icon name="sparkles" className="w-3 h-3" /> Home
                        </span>
                      )}
                      {p.bulkEligible && (
                        <span title="In the Bulk Vault" className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">
                          Bulk
                        </span>
                      )}
                    </div>
                    {p.description && (
                      <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{p.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{p.slug}</td>
                  <td className="px-4 py-3 text-gray-700">{variantCount}</td>
                  <td className="px-4 py-3 text-gray-700">{totalInventory}</td>
                  <td className="px-4 py-3">
                    <span className={p.isActive ? 'badge-green' : 'badge-gray'}>
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(p)}
                        className="text-xs text-tea-600 hover:text-tea-700 font-medium">Edit</button>
                      {p.isActive ? (
                        <button onClick={() => deactivate(p.id)}
                          className="text-xs text-red-600 hover:text-red-700 font-medium">Deactivate</button>
                      ) : (
                        <button onClick={() => reactivate(p)}
                          className="text-xs text-green-600 hover:text-green-700 font-medium">Reactivate</button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex gap-2 mt-4 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 rounded border text-sm ' +
                (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {editing && (
        <ProductForm
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {bulkPhoto && (
        <BulkPhotoModal
          onClose={() => setBulkPhoto(false)}
          onDone={() => { setBulkPhoto(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Commissions / Earnings management ───────────────────────────────────────
function CommissionsList() {
  const { fmt } = useCurrency({ usd: true });
  const [rows, setRows]             = useState([]);
  const [page, setPage]             = useState(1);
  const [pagination, setPagination] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing]       = useState(null); // commission id being edited
  const [draft, setDraft]           = useState({ amountDollars: '', status: '', notes: '' });
  const [saving, setSaving]         = useState(false);

  const load = () => {
    const params = { page };
    if (statusFilter) params.status = statusFilter;
    api.get('/admin/commissions', { params })
      .then(res => { setRows(res.data.data); setPagination(res.data.pagination); })
      .catch(() => toast.error('Failed to load commissions'));
  };

  useEffect(load, [page, statusFilter]);

  const startEdit = (c) => {
    setEditing(c.id);
    setDraft({
      amountDollars: (c.amountInCents / 100).toFixed(2),
      status: c.status,
      notes: c.notes || '',
    });
  };

  const cancelEdit = () => { setEditing(null); setDraft({ amountDollars: '', status: '', notes: '' }); };

  const saveEdit = async (c) => {
    setSaving(true);
    try {
      const payload = {
        amountInCents: Math.round(parseFloat(draft.amountDollars) * 100),
        status: draft.status,
        notes: draft.notes || null,
      };
      const res = await api.patch('/admin/commissions/' + c.id, payload);
      setRows(prev => prev.map(r => r.id === c.id ? { ...r, ...res.data } : r));
      toast.success('Commission updated');
      cancelEdit();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Commissions / Earnings</h2>
        <select
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="text-sm border border-gray-200 rounded px-2 py-1 bg-white">
          <option value="">All statuses</option>
          {['pending', 'approved', 'paid', 'cancelled'].map(s => (
            <option key={s} value={s}>{capitalize(s)}</option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              {['Affiliate', 'Order', 'Level', 'Rate', 'Amount', 'Status', 'Notes', 'Actions'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-semibold text-gray-600">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No commissions found.</td></tr>
            )}
            {rows.map(c => {
              const isEditing = editing === c.id;
              const locked = c.status === 'paid';
              return (
                <tr key={c.id} className="border-b last:border-0 hover:bg-gray-50 align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium flex items-center gap-1.5">
                      {c.affiliate?.firstName} {c.affiliate?.lastName}
                      {c.affiliate?.role === 'ambassador' && (
                        <span title="Ambassador — commission doubled" className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white">
                          2×
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">{c.affiliate?.email}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">{c.orderId?.slice(0, 8)}</td>
                  <td className="px-4 py-3 text-gray-700">L{c.level}</td>
                  <td className="px-4 py-3 text-gray-700">{(parseFloat(c.rate) * 100).toFixed(2)}%</td>
                  <td className="px-4 py-3 font-medium">
                    {isEditing ? (
                      <div className="flex items-center gap-1">
                        <input type="number" step="0.01" min="0"
                          value={draft.amountDollars}
                          onChange={e => setDraft(d => ({ ...d, amountDollars: e.target.value }))}
                          className="w-24 border border-gray-300 rounded px-2 py-1 text-sm" />
                        <span className="text-[10px] text-gray-500">USD</span>
                      </div>
                    ) : fmt(c.amountInCents)}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <select
                        value={draft.status}
                        onChange={e => setDraft(d => ({ ...d, status: e.target.value }))}
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
                        {['pending', 'approved', 'cancelled'].map(s => (
                          <option key={s} value={s}>{capitalize(s)}</option>
                        ))}
                      </select>
                    ) : (
                      <span className={statusBadge(c.status)}>{capitalize(c.status)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 max-w-xs">
                    {isEditing ? (
                      <input
                        value={draft.notes}
                        onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
                        placeholder="Adjustment reason..."
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm" />
                    ) : (
                      <span className="text-xs">{c.notes || '—'}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isEditing ? (
                      <div className="flex gap-2">
                        <button onClick={() => saveEdit(c)} disabled={saving}
                          className="text-xs text-tea-600 hover:text-tea-700 font-medium disabled:opacity-50">
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={cancelEdit}
                          className="text-xs text-gray-500 hover:text-gray-700">Cancel</button>
                      </div>
                    ) : (
                      <button onClick={() => startEdit(c)} disabled={locked}
                        title={locked ? 'Paid commissions are locked for audit' : ''}
                        className="text-xs text-tea-600 hover:text-tea-700 font-medium disabled:opacity-30 disabled:cursor-not-allowed">
                        {locked ? 'Locked' : 'Edit'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination.pages > 1 && (
        <div className="flex gap-2 mt-4 justify-end">
          {[...Array(pagination.pages)].map((_, i) => (
            <button key={i} onClick={() => setPage(i + 1)}
              className={'px-3 py-1 rounded border text-sm ' +
                (page === i + 1 ? 'bg-tea-600 text-white' : 'hover:border-tea-400')}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Media library ────────────────────────────────────────────────────────────
function FaviconManager() {
  const [favicon, setFavicon] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => {
    api.get('/uploads/favicon').then(res => setFavicon(res.data)).catch(() => {});
  };
  useEffect(load, []);

  const updatePageFavicon = (fav) => {
    const link = document.getElementById('app-favicon');
    if (!link) return;
    if (fav?.url) {
      link.setAttribute('type', fav.type || 'image/png');
      link.setAttribute('href', fav.url);
    } else {
      link.setAttribute('type', 'image/svg+xml');
      link.setAttribute('href', '/favicon.svg');
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('favicon', file);
      const res = await api.post('/uploads/favicon', fd);
      setFavicon(res.data);
      updatePageFavicon(res.data);
      toast.success('Favicon updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Revert to the default green-dot favicon?')) return;
    try {
      await api.delete('/uploads/favicon');
      setFavicon(null);
      updatePageFavicon(null);
      toast.success('Favicon reset to default');
    } catch {
      toast.error('Reset failed');
    }
  };

  const previewSrc = favicon?.url || '/favicon.svg';
  const isCustom = !!favicon?.url;

  return (
    <div className="bg-white rounded-xl border shadow-sm p-5 mb-6">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-16 h-16 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden shrink-0">
          <img src={previewSrc} alt="Favicon preview" className="w-12 h-12 object-contain" />
        </div>
        <div className="flex-1 min-w-[200px]">
          <h3 className="text-base font-bold text-gray-900">Site favicon</h3>
          <p className="text-xs text-gray-500 mt-1">
            Shown in browser tabs and bookmarks. PNG, SVG, ICO, WebP, or GIF — under 512 KB. Square images (32×32 or larger) work best.
          </p>
          <div className="text-xs text-gray-400 mt-1">
            {isCustom ? `Currently using uploaded ${favicon.filename}` : 'Currently using the default green dot'}
          </div>
        </div>
        <div className="flex gap-2 items-center">
          <input ref={inputRef} type="file"
            accept="image/png,image/svg+xml,image/x-icon,image/vnd.microsoft.icon,image/webp,image/gif"
            className="hidden" onChange={handleUpload} />
          <button disabled={uploading} onClick={() => inputRef.current?.click()}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {uploading ? 'Uploading…' : isCustom ? 'Replace favicon' : 'Upload favicon'}
          </button>
          {isCustom && (
            <button onClick={handleReset}
              className="text-sm font-medium text-gray-600 hover:text-red-600 px-3 py-2">
              Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Certificates of Analysis (COA) management ────────────────────────────────
function COAManager() {
  const [coas, setCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/coa')
      .then(res => setCoas(res.data))
      .catch(() => toast.error('Failed to load COAs'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Choose a file to upload'); return; }
    if (!title.trim()) { toast.error('Enter a title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      fd.append('file', file);
      await api.post('/coa', fd);
      toast.success('COA uploaded');
      setTitle('');
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (coa) => {
    if (!window.confirm(`Delete "${coa.title}"? This removes it from the public COA page.`)) return;
    try {
      await api.delete('/coa/' + coa.id);
      toast.success('Deleted');
      setCoas(prev => prev.filter(c => c.id !== coa.id));
    } catch {
      toast.error('Delete failed');
    }
  };

  const isImage = (m) => typeof m === 'string' && m.startsWith('image/');

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold">Certificates of Analysis</h2>
        <p className="text-sm text-gray-500 mt-1">
          Uploaded COAs appear on the public <span className="font-medium">/coa</span> page (linked from Compliance), newest first.
        </p>
      </div>

      {/* Upload form */}
      <form onSubmit={handleUpload} className="bg-white border rounded-xl p-5 mb-8 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. BPC-157 5mg — Batch #A2406"
            maxLength={200}
            className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-tea-500"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">File (PDF or image, max 20 MB)</label>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,image/jpeg,image/png,image/webp"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-tea-50 file:text-tea-700 hover:file:bg-tea-100"
          />
        </div>
        <button
          type="submit"
          disabled={uploading}
          className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
          {uploading ? 'Uploading…' : '+ Upload COA'}
        </button>
      </form>

      {loading && <div className="py-12 text-center text-gray-400">Loading…</div>}

      {!loading && coas.length === 0 && (
        <div className="py-16 text-center bg-white rounded-xl border">
          <Icon name="coa" className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">No certificates uploaded yet.</p>
        </div>
      )}

      {!loading && coas.length > 0 && (
        <div className="bg-white border rounded-xl divide-y">
          {coas.map(coa => (
            <div key={coa.id} className="flex items-center gap-4 p-4">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-tea-50 text-tea-700">
                <Icon name={isImage(coa.mimeType) ? 'media' : 'doc'} className="w-5 h-5" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate" title={coa.title}>{coa.title}</p>
                <p className="text-xs text-gray-400">
                  {formatDate(coa.createdAt)}
                  {coa.size ? ` · ${(coa.size / 1024).toFixed(0)} KB` : ''}
                </p>
              </div>
              <a href={coa.url} target="_blank" rel="noopener noreferrer"
                className="text-tea-700 hover:text-tea-800 text-sm font-medium">
                View
              </a>
              <button onClick={() => handleDelete(coa)}
                className="text-red-600 hover:text-red-700 text-sm font-medium">
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MediaLibrary() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get('/uploads')
      .then(res => setFiles(res.data))
      .catch(() => toast.error('Failed to load media'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append('image', file);
        await api.post('/uploads', fd);
      }
      toast.success(files.length === 1 ? 'Image uploaded' : `${files.length} images uploaded`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Delete ${filename}?`)) return;
    try {
      await api.delete('/uploads/' + filename);
      toast.success('Deleted');
      setFiles(prev => prev.filter(f => f.filename !== filename));
    } catch {
      toast.error('Delete failed');
    }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => toast.success('URL copied'));
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Media Library</h2>
        <div>
          <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden"
            onChange={handleUpload} />
          <button disabled={uploading} onClick={() => fileInputRef.current?.click()}
            className="bg-tea-600 hover:bg-tea-700 text-white text-sm font-medium px-4 py-2 rounded-lg disabled:opacity-50">
            {uploading ? 'Uploading…' : '+ Upload images'}
          </button>
        </div>
      </div>

      <FaviconManager />

      {loading && <div className="py-12 text-center text-gray-400">Loading…</div>}

      {!loading && files.length === 0 && (
        <div className="py-16 text-center bg-white rounded-xl border">
          <Icon name="media" className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 text-sm">No images uploaded yet.</p>
          <button onClick={() => fileInputRef.current?.click()}
            className="mt-4 text-tea-600 hover:text-tea-700 text-sm font-medium">Upload your first image</button>
        </div>
      )}

      {!loading && files.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {files.map(f => (
            <div key={f.filename} className="group relative bg-white border rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow">
              <div className="aspect-square bg-gray-50">
                <img src={f.url} alt={f.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <p className="text-xs text-gray-500 truncate" title={f.filename}>{f.filename}</p>
                <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(0)} KB</p>
              </div>
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                <button onClick={() => copyUrl(f.url)}
                  className="bg-white text-gray-800 text-xs font-medium px-2 py-1 rounded-lg hover:bg-gray-100">
                  Copy URL
                </button>
                <button onClick={() => handleDelete(f.filename)}
                  className="bg-red-600 text-white text-xs font-medium px-2 py-1 rounded-lg hover:bg-red-700">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Labeled field helper (shared by the settings forms) ──────────────────────
function LabeledField({ label, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}

// ─── Store rewards (free shipping + gift ladder) ──────────────────────────────
// Edits the same single SiteSettings row as MaintenanceSettings, via the shared
// /admin/site-settings endpoint. Money is stored in cents but shown/edited in
// dollars. Drives the storefront cart ladder, the shipping line, and the promo
// banner (see useRewardConfig on the frontend; GET /api/site/status serves it).

const centsToDollars = (c) => (c === null || c === undefined || c === '' ? '' : (Number(c) / 100).toFixed(2));
const dollarsToCents = (d) => Math.round((parseFloat(d) || 0) * 100);

function RewardSettings() {
  const [row, setRow] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setLoadError('');
    try {
      const res = await api.get('/admin/site-settings');
      setRow(res.data);
      if (!silent) setEdits({});
    } catch (err) {
      if (silent) return;
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message;
      if (status === 500 || /relation .* does not exist|no such table|Unknown column|SiteSettings|free_shipping/i.test(detail)) {
        setLoadError('The rewards columns are missing. Run the new migration:\n\ndocker exec ab8beauty-retea3-backend-1 npx sequelize-cli db:migrate\n\nthen restart the backend and refresh this page.');
      } else {
        setLoadError(`Failed to load reward settings (${status || 'network'}): ${detail}`);
      }
      toast.error('Failed to load reward settings');
    }
  };
  useEffect(() => { reload(); }, []);

  // `edits` is keyed by the real API field names in API units (cents / bool /
  // string), so it can be PATCHed straight through.
  const setField = (f, v) => setEdits(prev => ({ ...prev, [f]: v }));
  const cents = (f) => (edits[f] !== undefined ? edits[f] : row?.[f] ?? 0);
  const text  = (f) => (edits[f] !== undefined ? edits[f] : row?.[f] ?? '');
  const bool  = (f) => (edits[f] !== undefined ? edits[f] : !!row?.[f]);

  const save = async () => {
    if (!Object.keys(edits).length) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await api.patch('/admin/site-settings', edits);
      setRow(res.data);
      setEdits({});
      toast.success('Rewards saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loadError) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold mb-3">Rewards</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="font-semibold text-red-800 mb-2">Setup required</div>
          <pre className="whitespace-pre-wrap text-sm text-red-700 font-mono">{loadError}</pre>
          <button onClick={reload} className="btn-secondary mt-4 text-sm">Retry</button>
        </div>
      </div>
    );
  }
  if (!row) return <div className="py-12 text-center text-gray-400">Loading…</div>;

  // A dollar-denominated input bound to a *_Cents field on `edits`.
  const DollarField = ({ field, label, hint }) => (
    <LabeledField label={label} hint={hint}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
        <input type="number" min="0" step="0.01" inputMode="decimal"
          className="input-field pl-7"
          value={centsToDollars(cents(field))}
          onChange={e => setField(field, dollarsToCents(e.target.value))} />
      </div>
    </LabeledField>
  );

  const giftsOn = bool('freeGiftEnabled');

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Rewards</h2>
      <p className="text-sm text-gray-500 mb-6 max-w-2xl">
        Tune the perks shoppers unlock as their cart grows. Changes go live across the
        storefront — the cart rewards ladder, the checkout shipping line and the promo
        banner — as soon as you save.
      </p>

      {/* ── Free shipping ─────────────────────────────────────────── */}
      <div className="bg-white border rounded-xl shadow-sm p-6 max-w-2xl space-y-4 mb-6">
        <div className="flex items-center gap-2">
          <Icon name="truck" className="w-5 h-5 text-tea-600" />
          <div className="text-sm font-semibold text-gray-700">Free shipping</div>
        </div>
        <DollarField field="freeShippingThresholdCents" label="Free shipping over"
          hint="Orders at or above this subtotal ship free. Set to $0 to make all shipping free." />
        <DollarField field="flatShippingRateCents" label="Flat shipping rate"
          hint="Charged on orders below the threshold above." />
      </div>

      {/* ── Free gifts ladder ─────────────────────────────────────── */}
      <div className="bg-white border rounded-xl shadow-sm p-6 max-w-2xl space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="gift" className="w-5 h-5 text-pink-500" />
            <div className="text-sm font-semibold text-gray-700">Free gifts</div>
          </div>
          <button
            onClick={() => setField('freeGiftEnabled', !giftsOn)}
            className={'relative inline-flex h-6 w-11 items-center rounded-full transition-colors ' +
              (giftsOn ? 'bg-tea-600' : 'bg-gray-300')}
            aria-pressed={giftsOn} aria-label="Toggle free gifts">
            <span className={'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ' +
              (giftsOn ? 'translate-x-5' : 'translate-x-1')} />
          </button>
        </div>

        {giftsOn ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-pink-100 bg-pink-50/50 p-4 space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-pink-600">First gift</div>
              <LabeledField label="Gift name" hint="Shown in the cart when unlocked.">
                <input className="input-field" value={text('freeGiftName') || ''}
                  onChange={e => setField('freeGiftName', e.target.value)}
                  placeholder="Free Bubblé balm stick" />
              </LabeledField>
              <DollarField field="freeGiftThresholdCents" label="Unlocks over"
                hint="Cart subtotal that earns this gift." />
              <DollarField field="freeGiftValueCents" label="Retail value"
                hint="Shown struck-through next to the gift." />
            </div>

            <div className="rounded-lg border border-amber-100 bg-amber-50/50 p-4 space-y-4">
              <div className="text-xs font-bold uppercase tracking-wide text-amber-600">Second (mystery) gift</div>
              <LabeledField label="Gift name" hint="The top reward on the ladder.">
                <input className="input-field" value={text('mysteryGiftName') || ''}
                  onChange={e => setField('mysteryGiftName', e.target.value)}
                  placeholder="Mystery wellness gift" />
              </LabeledField>
              <DollarField field="mysteryGiftThresholdCents" label="Unlocks over"
                hint="Cart subtotal that earns the mystery gift." />
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-400">
            Free gifts are turned off — the cart shows only the free-shipping reward.
          </p>
        )}

        <div className="flex items-center gap-3 pt-4 border-t">
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : 'Save rewards'}
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            Last updated {formatDate(row.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Maintenance mode (site pause) ────────────────────────────────────────────

function MaintenanceSettings() {
  const [row, setRow] = useState(null);
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState('');

  const reload = async ({ silent = false } = {}) => {
    if (!silent) setLoadError('');
    try {
      const res = await api.get('/admin/site-settings');
      setRow(res.data);
      if (!silent) setEdits({});
    } catch (err) {
      if (silent) return;
      const status = err.response?.status;
      const detail = err.response?.data?.error || err.message;
      if (status === 500 || /relation .* does not exist|no such table|SiteSettings/i.test(detail)) {
        setLoadError('The SiteSettings database table is missing. Run the new migration:\n\ndocker exec ab8beauty-retea3-backend-1 npx sequelize-cli db:migrate\n\nthen restart the backend and refresh this page.');
      } else {
        setLoadError(`Failed to load site settings (${status || 'network'}): ${detail}`);
      }
      toast.error('Failed to load maintenance settings');
    }
  };
  useEffect(() => {
    reload();
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') reload({ silent: true });
    }, 20000);
    return () => clearInterval(id);
  }, []);

  const setField = (f, v) => setEdits(prev => ({ ...prev, [f]: v }));
  const val = (f) => (edits[f] !== undefined ? edits[f] : row?.[f] ?? '');

  const save = async () => {
    if (!Object.keys(edits).length) { toast.info('No changes to save'); return; }
    setSaving(true);
    try {
      const res = await api.patch('/admin/site-settings', edits);
      setRow(res.data);
      setEdits({});
      toast.success('Holding-page text saved');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const toggleMaintenance = async () => {
    if (!row) return;
    const next = !row.maintenanceMode;
    // Persist any in-progress copy edits alongside the toggle so the holding
    // page reflects what the admin sees in the form.
    const payload = { ...edits, maintenanceMode: next };
    try {
      const res = await api.patch('/admin/site-settings', payload);
      setRow(res.data);
      setEdits({});
      toast.success(next ? 'Site is now paused' : 'Site is back online');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Toggle failed');
    }
  };

  if (loadError) {
    return (
      <div className="max-w-2xl">
        <h2 className="text-2xl font-bold mb-3">Maintenance Mode</h2>
        <div className="bg-red-50 border border-red-200 rounded-xl p-5">
          <div className="font-semibold text-red-800 mb-2">Setup required</div>
          <pre className="whitespace-pre-wrap text-sm text-red-700 font-mono">{loadError}</pre>
          <button onClick={reload} className="btn-secondary mt-4 text-sm">Retry</button>
        </div>
      </div>
    );
  }
  if (!row) return <div className="py-12 text-center text-gray-400">Loading…</div>;

  const on = row.maintenanceMode;

  return (
    <div>
      <h2 className="text-2xl font-bold mb-2">Maintenance Mode</h2>
      <p className="text-sm text-gray-500 mb-6">
        Pause the storefront and show every visitor a branded “we’ll be back shortly” page.
        You and other admins keep full access and can lift the pause anytime.
      </p>

      {/* Status / master switch */}
      <div className={'rounded-xl border p-5 mb-6 max-w-2xl flex items-center gap-4 ' +
        (on ? 'bg-amber-50 border-amber-300' : 'bg-white')}>
        <Icon name={on ? 'maintenance' : 'check'} className={'w-8 h-8 shrink-0 ' + (on ? 'text-amber-500' : 'text-emerald-500')} />
        <div className="flex-1">
          <div className="font-semibold text-gray-900">
            {on ? 'The site is paused' : 'The site is live'}
          </div>
          <div className="text-sm text-gray-500">
            {on
              ? 'Visitors see the holding page. Admins and the /login page are unaffected.'
              : 'Everything is running normally for all visitors.'}
          </div>
        </div>
        <button
          onClick={toggleMaintenance}
          className={'px-5 py-2.5 rounded-lg font-semibold text-white shadow-sm ' +
            (on ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700')}
        >
          {on ? 'Bring site back online' : 'Pause the site'}
        </button>
      </div>

      {/* Holding-page copy */}
      <div className="bg-white border rounded-xl shadow-sm p-6 max-w-2xl space-y-4">
        <div className="text-sm font-semibold text-gray-700">Holding-page text</div>
        <LabeledField label="Heading">
          <input className="input-field" value={val('maintenanceHeading') || ''}
            onChange={e => setField('maintenanceHeading', e.target.value)}
            placeholder="We'll be back shortly" />
        </LabeledField>
        <LabeledField label="Message">
          <textarea className="input-field min-h-[110px]" value={val('maintenanceMessage') || ''}
            onChange={e => setField('maintenanceMessage', e.target.value)}
            placeholder="AB8 Beauty is taking a short break for scheduled maintenance…" />
        </LabeledField>
        <LabeledField label="Expected back (optional)" hint="Shown as a small badge, e.g. “in about an hour” or “Monday 9am ET”.">
          <input className="input-field" value={val('maintenanceEta') || ''}
            onChange={e => setField('maintenanceEta', e.target.value)}
            placeholder="in about an hour" />
        </LabeledField>

        <div className="flex items-center gap-3 pt-4 border-t">
          <button onClick={save} disabled={saving} className="btn-primary disabled:opacity-50">
            {saving ? 'Saving…' : 'Save text'}
          </button>
          <span className="text-xs text-gray-400 ml-auto">
            Last updated {formatDate(row.updatedAt)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ─── Logistics Command Center ─────────────────────────────────────────────────

function InvStatusBadge({ status }) {
  const cfg = {
    out:      'bg-red-100 text-red-700 border-red-200',
    critical: 'bg-red-100 text-red-700 border-red-200',
    low:      'bg-amber-100 text-amber-700 border-amber-200',
    watch:    'bg-yellow-100 text-yellow-700 border-yellow-200',
    ok:       'bg-emerald-100 text-emerald-700 border-emerald-200',
  };
  const lbl = { out: 'OUT', critical: 'CRITICAL', low: 'LOW', watch: 'WATCH', ok: 'OK' };
  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${cfg[status] || cfg.ok}`}>
      {lbl[status] || 'OK'}
    </span>
  );
}

function LogSectionHeader({ icon, title, subtitle }) {
  return (
    <div className="mb-4">
      <div className="flex items-center gap-2.5">
        <span className="grid place-items-center w-9 h-9 rounded-xl bg-tea-50 text-tea-600"><Icon name={icon} className="w-5 h-5" /></span>
        <h2 className="text-lg font-bold text-gray-900">{title}</h2>
      </div>
      {subtitle && <p className="text-xs text-gray-400 mt-0.5 pl-[46px]">{subtitle}</p>}
    </div>
  );
}

function BestSellersSection({ sellers }) {
  const { fmt } = useCurrency({ usd: true });
  const rankBadge = (rank) => {
    const medal = { 1: 'bg-gradient-to-br from-amber-300 to-amber-500 text-white ring-2 ring-amber-200', 2: 'bg-gradient-to-br from-gray-300 to-gray-400 text-white ring-2 ring-gray-200', 3: 'bg-gradient-to-br from-orange-300 to-orange-500 text-white ring-2 ring-orange-200' }[rank];
    return (
      <span className={'w-8 h-8 flex items-center justify-center text-xs font-extrabold rounded-full shrink-0 shadow-sm ' +
        (medal || 'bg-gray-100 text-gray-500')}>
        {rank}
      </span>
    );
  };

  const trendEl = (dir, pct) => {
    if (dir === 'up')   return <span className="text-emerald-600 font-semibold text-xs">▲ {pct}% this month</span>;
    if (dir === 'down') return <span className="text-red-500 font-semibold text-xs">▼ {Math.abs(pct)}% this month</span>;
    return <span className="text-gray-400 text-xs">→ Stable demand</span>;
  };

  const invBar = (inv, status) => {
    const pct = Math.min(100, Math.round((inv / 100) * 100));
    const barColor = { out: 'bg-red-500', critical: 'bg-red-400', low: 'bg-amber-400', watch: 'bg-yellow-400', ok: 'bg-emerald-500' };
    return (
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${barColor[status] || 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs text-gray-500 w-12 text-right shrink-0">{inv} left</span>
      </div>
    );
  };

  const rec = (s) => {
    if (s.inventoryStatus === 'out')                              return { icon: 'warning', text: 'Out of Stock — Restock Now',               cls: 'text-red-600 bg-red-50 border-red-200' };
    if (s.inventoryStatus === 'critical')                         return { icon: 'warning', text: 'Critical — Urgent Restock Needed',         cls: 'text-red-600 bg-red-50 border-red-200' };
    if (s.inventoryStatus === 'low' && s.trendDir === 'up')       return { icon: 'forecast', text: 'Rising Demand + Low Stock — Restock Soon', cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (s.inventoryStatus === 'low')                              return { icon: 'orders', text: 'Low Stock — Plan Restock',                 cls: 'text-amber-700 bg-amber-50 border-amber-200' };
    if (s.trendDir === 'up')                                      return { icon: 'flame', text: 'Demand Rising — Monitor Closely',          cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
    return                                                               { icon: 'check', text: 'Stock Levels Good',                       cls: 'text-emerald-700 bg-emerald-50 border-emerald-200' };
  };

  return (
    <div>
      <LogSectionHeader icon="flame" title="Best Sellers & Trending"
        subtitle="Top products by 90-day sales velocity" />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sellers.map(s => {
          const r = rec(s);
          return (
            <div key={s.variantId}
              className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5">
              <div className="flex items-start gap-3 mb-4">
                <div className="shrink-0">{rankBadge(s.rank)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 text-sm leading-tight truncate">{s.productName}</div>
                  <div className="text-xs text-gray-500 truncate">{s.variantName}</div>
                  <div className="text-xs text-gray-400 font-mono">SKU: {s.sku}</div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="text-xl font-extrabold text-gray-800 leading-none">{s.unitsSold90d}</div>
                  <div className="text-xs text-gray-400 mt-0.5">units / 90d</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                  <div className="text-sm font-extrabold text-gray-800 leading-none">{fmt(s.revenue90d)}</div>
                  <div className="text-xs text-gray-400 mt-0.5">revenue</div>
                </div>
              </div>
              <div className="mb-3">{trendEl(s.trendDir, s.trendPct)}</div>
              <div className="mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-500">Inventory</span>
                  <InvStatusBadge status={s.inventoryStatus} />
                </div>
                {invBar(s.inventory, s.inventoryStatus)}
              </div>
              <div className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border ${r.cls}`}><Icon name={r.icon} className="w-3.5 h-3.5 shrink-0" /> {r.text}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PendingShipmentsTable({ shipments }) {
  const { fmt } = useCurrency({ usd: true });
  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <LogSectionHeader icon="orders" title="Pending Shipments"
          subtitle="Orders awaiting tracking numbers — oldest first" />
        <Link to="/admin/orders"
          className="shrink-0 inline-flex items-center gap-1 text-sm font-semibold text-tea-600 hover:text-tea-700 bg-tea-50 hover:bg-tea-100 px-3 py-1.5 rounded-lg transition-colors mt-1">
          View All Orders <Icon name="back" className="w-4 h-4 rotate-180" />
        </Link>
      </div>
      {shipments.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center shadow-sm">
          <Icon name="check" className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
          <div className="font-bold text-gray-700">All caught up!</div>
          <div className="text-gray-400 text-sm mt-1">Every paid order has a tracking number</div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3 bg-amber-50 border-b border-amber-100 flex items-center gap-2">
            <span className="text-amber-600 text-sm font-bold">
              {shipments.length} order{shipments.length !== 1 ? 's' : ''} need tracking
            </span>
            <span className="text-amber-400 text-xs">— add tracking numbers in the Orders tab</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm whitespace-nowrap">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-400 uppercase tracking-wide border-b">
                  <th className="text-left px-4 py-2.5">Customer</th>
                  <th className="text-left px-4 py-2.5">Items</th>
                  <th className="text-right px-4 py-2.5">Total</th>
                  <th className="text-left px-4 py-2.5">Payment</th>
                  <th className="text-left px-4 py-2.5">Ordered</th>
                  <th className="text-center px-4 py-2.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {shipments.map(o => (
                  <tr key={o.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{o.customer}</div>
                      <div className="text-xs text-gray-400">{o.customerEmail}</div>
                    </td>
                    <td className="px-4 py-3 max-w-xs">
                      {o.items.map((i, idx) => (
                        <div key={idx} className="text-xs text-gray-600">
                          <span className="font-semibold">{i.quantity}×</span> {i.productName}
                          {i.variantName && <span className="text-gray-400"> ({i.variantName})</span>}
                        </div>
                      ))}
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-gray-800">{fmt(o.totalInCents)}</td>
                    <td className="px-4 py-3 text-gray-500 capitalize">{o.paymentMethod}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(o.createdAt)}</td>
                    <td className="px-4 py-3 text-center">{statusBadge(o.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfitabilitySection({ data }) {
  const { fmt } = useCurrency({ usd: true });
  const [scope, setScope] = useState('all'); // 'all' | '30d'
  const { coverage, allTime, last30d, variants } = data;

  const scopeCfg = {
    all:  { label: 'All-time',           rev: allTime.revenueInCents,           cogs: allTime.cogsInCents,           profit: allTime.profitInCents,           margin: allTime.marginPct },
    '30d': { label: 'Last 30 days',       rev: last30d.revenueInCents,           cogs: last30d.cogsInCents,           profit: last30d.profitInCents,           margin: last30d.marginPct },
  };
  const s = scopeCfg[scope];

  const marginColor = (pct) =>
    pct == null ? 'text-gray-400'
    : pct >= 50 ? 'text-emerald-700'
    : pct >= 25 ? 'text-amber-700'
    : 'text-red-700';

  const marginBg = (pct) =>
    pct == null ? 'bg-gray-50 border-gray-200'
    : pct >= 50 ? 'bg-emerald-50 border-emerald-200'
    : pct >= 25 ? 'bg-amber-50 border-amber-200'
    : 'bg-red-50 border-red-200';

  const sortedVariants = [...variants]
    .sort((a, b) => {
      const aKey = scope === 'all' ? a.profitAllTimeInCents
                 : scope === '30d' ? a.profit30dInCents
                 : a.subProfitPerMonthInCents;
      const bKey = scope === 'all' ? b.profitAllTimeInCents
                 : scope === '30d' ? b.profit30dInCents
                 : b.subProfitPerMonthInCents;
      return (bKey || 0) - (aKey || 0);
    });

  const missingCount = coverage.totalVariants - coverage.variantsWithCost;

  return (
    <div>
      <LogSectionHeader icon="revenue" title="Profitability"
        subtitle="Revenue, cost of goods, gross profit & margin per variant — set unit cost on each product to populate" />

      {missingCount > 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
          <Icon name="warning" className="w-5 h-5 shrink-0 text-amber-500" />
          <div>
            <div className="font-bold">
              {coverage.variantsWithCost} of {coverage.totalVariants} variants have a cost set.
            </div>
            <div className="text-amber-700 mt-0.5">
              Add a <strong>Cost ($)</strong> to each variant on the Products tab to unlock accurate profit numbers.
              {coverage.variantsMissingCost.length > 0 && (
                <span> Variants missing cost that have sold:&nbsp;
                  <span className="font-mono text-xs">
                    {coverage.variantsMissingCost.slice(0, 5).map(v => v.sku).join(', ')}
                    {coverage.variantsMissingCost.length > 5 ? ` +${coverage.variantsMissingCost.length - 5} more` : ''}
                  </span>
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Scope toggle */}
      <div className="inline-flex bg-gray-100 rounded-lg p-1 mb-4">
        {[
          { id: 'all',  label: 'All-time' },
          { id: '30d',  label: 'Last 30 days' },
        ].map(opt => (
          <button key={opt.id} onClick={() => setScope(opt.id)}
            className={'px-4 py-1.5 text-sm font-semibold rounded-md transition-colors ' +
              (scope === opt.id ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700')}>
            {opt.label}
          </button>
        ))}
      </div>

      {/* Headline cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Revenue</div>
          <div className="text-2xl font-extrabold text-gray-900 mt-1">{fmt(s.rev)}</div>
          <div className="text-xs text-gray-400 mt-1">{s.label}</div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">COGS</div>
          <div className="text-2xl font-extrabold text-gray-700 mt-1">{fmt(s.cogs)}</div>
          <div className="text-xs text-gray-400 mt-1">cost of goods sold</div>
        </div>
        <div className={'rounded-xl border p-5 shadow-sm ' + marginBg(s.margin)}>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Gross Profit</div>
          <div className={'text-2xl font-extrabold mt-1 ' + marginColor(s.margin)}>{fmt(s.profit)}</div>
          <div className="text-xs text-gray-400 mt-1">revenue − COGS</div>
        </div>
        <div className={'rounded-xl border p-5 shadow-sm ' + marginBg(s.margin)}>
          <div className="text-xs font-bold text-gray-500 uppercase tracking-wide">Margin</div>
          <div className={'text-2xl font-extrabold mt-1 ' + marginColor(s.margin)}>
            {s.margin == null ? '—' : `${s.margin}%`}
          </div>
          <div className="text-xs text-gray-400 mt-1">gross margin</div>
        </div>
      </div>

      {/* Per-variant table */}
      <div className="overflow-x-auto bg-white rounded-xl border shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr className="text-xs text-gray-500 uppercase tracking-wide">
              <th className="text-left px-4 py-2.5 font-semibold">Product / Variant</th>
              <th className="text-right px-3 py-2.5 font-semibold">Price</th>
              <th className="text-right px-3 py-2.5 font-semibold">Cost</th>
              <th className="text-right px-3 py-2.5 font-semibold">Unit Profit</th>
              <th className="text-right px-3 py-2.5 font-semibold">Units</th>
              <th className="text-right px-3 py-2.5 font-semibold">Revenue</th>
              <th className="text-right px-3 py-2.5 font-semibold">Profit</th>
              <th className="text-right px-3 py-2.5 font-semibold">Margin</th>
            </tr>
          </thead>
          <tbody>
            {sortedVariants.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No variants yet.</td></tr>
            )}
            {sortedVariants.map(v => {
              const units   = scope === 'all' ? v.unitsAllTime         : scope === '30d' ? v.units30d         : v.monthlySubUnits;
              const revenue = scope === 'all' ? v.revenueAllTimeInCents : scope === '30d' ? v.revenue30dInCents : v.subRevenuePerMonthInCents;
              const profit  = scope === 'all' ? v.profitAllTimeInCents  : scope === '30d' ? v.profit30dInCents  : v.subProfitPerMonthInCents;
              const margin  = !v.hasCost ? null
                            : scope === 'all' ? v.marginAllTimePct
                            : scope === '30d' ? v.margin30dPct
                            : v.unitMarginPct;
              return (
                <tr key={v.variantId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="font-semibold text-gray-900">{v.productName}</div>
                    <div className="text-xs text-gray-400 font-mono">{v.variantName} · {v.sku}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(v.priceInCents)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className="text-gray-700">{fmt(v.costInCents)}</span>
                      : <span className="text-amber-600 text-xs font-semibold">set cost</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className={'font-semibold ' + marginColor(v.unitMarginPct)}>{fmt(v.unitProfitInCents)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{units || 0}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-gray-700">{fmt(revenue || 0)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {v.hasCost
                      ? <span className={'font-bold ' + marginColor(margin)}>{fmt(profit || 0)}</span>
                      : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right tabular-nums">
                    {margin == null
                      ? <span className="text-gray-300">—</span>
                      : <span className={'font-bold ' + marginColor(margin)}>{margin}%</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InventoryAlertsSection({ alerts }) {
  const statusCfg = {
    out:      { bg: 'bg-red-50 border-red-300',    dot: 'bg-red-600',   label: 'OUT OF STOCK', tc: 'text-red-700'   },
    critical: { bg: 'bg-red-50 border-red-200',    dot: 'bg-red-500',   label: 'CRITICAL',     tc: 'text-red-600'   },
    low:      { bg: 'bg-amber-50 border-amber-200', dot: 'bg-amber-400', label: 'LOW STOCK',   tc: 'text-amber-700' },
  };
  return (
    <div>
      <LogSectionHeader icon="warning" title="Inventory Alerts"
        subtitle="Active variants below 20 units — plan restocking before next billing cycle" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {alerts.map(a => {
          const cfg = statusCfg[a.status] || statusCfg.low;
          return (
            <div key={a.variantId} className={`rounded-xl border p-4 shadow-sm ${cfg.bg}`}>
              <div className="flex items-start justify-between mb-2">
                <span className={`grid place-items-center w-8 h-8 rounded-lg bg-white/70 ${cfg.tc}`}><Icon name="warning" className="w-4 h-4" /></span>
                <span className={`inline-flex items-center gap-1 text-xs font-extrabold px-2 py-0.5 rounded border border-current bg-white/60 ${cfg.tc}`}>
                  <StatusDot color={cfg.dot} /> {cfg.label}
                </span>
              </div>
              <div className={`font-bold text-sm leading-tight ${cfg.tc}`}>{a.productName}</div>
              <div className="text-xs text-gray-600 mt-0.5">{a.variantName}</div>
              <div className="text-xs text-gray-400 font-mono mt-0.5">SKU: {a.sku}</div>
              <div className="mt-3 flex items-end gap-1.5">
                <span className={`text-3xl font-extrabold leading-none ${cfg.tc}`}>{a.inventory}</span>
                <span className="text-xs text-gray-500 pb-0.5">units remaining</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LogisticsCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setLoadError(false);
    api.get('/admin/logistics/dashboard')
      .then(res => setData(res.data))
      .catch(() => { toast.error('Failed to load logistics data'); setLoadError(true); })
      .finally(() => { setLoading(false); setRefreshing(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="py-20 text-center">
        <Icon name="logistics" className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <div className="font-bold text-gray-600 text-lg">Loading logistics data…</div>
        <div className="text-gray-400 text-sm mt-1">Crunching inventory &amp; orders</div>
      </div>
    );
  }

  if (loadError || !data) {
    return (
      <div className="py-20 text-center">
        <Icon name="warning" className="w-12 h-12 mx-auto mb-4 text-amber-400" />
        <div className="font-bold text-gray-600 text-lg mb-4">Could not load logistics data</div>
        <button onClick={() => load(false)} className="px-4 py-2 bg-tea-600 text-white rounded-lg text-sm font-medium hover:bg-tea-700">
          Retry
        </button>
      </div>
    );
  }

  const { bestSellers, pendingShipments, inventoryAlerts, profitability, generatedAt } = data;
  const critCount = inventoryAlerts.filter(a => a.status === 'critical' || a.status === 'out').length;
  const lowCount  = inventoryAlerts.filter(a => a.status === 'low').length;

  const kpiCards = [
    {
      label: 'Pending Shipments', value: pendingShipments.length,
      sub: pendingShipments.length > 0 ? 'need tracking numbers' : 'all shipped',
      icon: 'orders', color: pendingShipments.length > 0 ? 'amber' : 'green',
    },
    {
      label: 'Stock Alerts', value: inventoryAlerts.length,
      sub: critCount > 0 ? `${critCount} critical` : lowCount > 0 ? `${lowCount} low` : 'all levels OK',
      icon: 'warning', color: critCount > 0 ? 'red' : lowCount > 0 ? 'amber' : 'green',
    },
  ];

  const kpiColors = {
    green:  { card: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700' },
    amber:  { card: 'bg-amber-50 border-amber-200',     text: 'text-amber-700'   },
    red:    { card: 'bg-red-50 border-red-200',         text: 'text-red-600'     },
    indigo: { card: 'bg-indigo-50 border-indigo-200',   text: 'text-indigo-700'  },
  };

  return (
    <div className="space-y-8">

      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 text-white shadow-2xl ring-1 ring-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-white/10 ring-1 ring-white/15"><Icon name="logistics" className="w-5 h-5" /></span>
              <h1 className="text-2xl font-extrabold tracking-tight">Logistics Command Center</h1>
            </div>
            <p className="text-slate-400 text-sm pl-[52px]">
              Master fulfillment intelligence — stock velocity &amp; shipments
            </p>
          </div>
          <div className="shrink-0 text-right">
            <button onClick={() => load(true)} disabled={refreshing}
              className="flex items-center gap-1.5 bg-white/10 hover:bg-white/20 border border-white/20 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-all disabled:opacity-40 mb-2">
              <Icon name="pulse" className={'w-4 h-4 ' + (refreshing ? 'animate-spin' : '')} />
              {refreshing ? 'Syncing…' : 'Refresh'}
            </button>
            {generatedAt && (
              <div className="text-slate-500 text-xs">
                Synced {new Date(generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
          </div>
        </div>

        {(critCount > 0 || lowCount > 0 || pendingShipments.length > 0) && (
          <div className="mt-4 flex flex-wrap gap-2">
            {critCount > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-red-500/20 border border-red-400/40 text-red-300 text-xs font-bold px-3 py-1 rounded-full">
                <StatusDot color="bg-red-400" /> {critCount} Critical Stock {critCount > 1 ? 'Alerts' : 'Alert'}
              </span>
            )}
            {lowCount > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-amber-400/20 border border-amber-400/40 text-amber-300 text-xs font-bold px-3 py-1 rounded-full">
                <StatusDot color="bg-amber-400" /> {lowCount} Low Stock {lowCount > 1 ? 'Warnings' : 'Warning'}
              </span>
            )}
            {pendingShipments.length > 0 && (
              <span className="inline-flex items-center gap-1.5 bg-sky-500/20 border border-sky-400/40 text-sky-300 text-xs font-bold px-3 py-1 rounded-full">
                <Icon name="orders" className="w-3.5 h-3.5" /> {pendingShipments.length} {pendingShipments.length === 1 ? 'Order' : 'Orders'} Awaiting Tracking
              </span>
            )}
          </div>
        )}
      </div>

      {/* KPI bar */}
      <div className="grid grid-cols-2 gap-4">
        {kpiCards.map(card => {
          const c = kpiColors[card.color] || kpiColors.indigo;
          return (
            <div key={card.label} className={`rounded-xl border p-5 shadow-sm ${c.card}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className={`text-2xl font-extrabold leading-none ${c.text}`}>{card.value}</div>
                  <div className="text-xs font-bold text-gray-700 mt-1">{card.label}</div>
                  {card.sub && <div className="text-xs text-gray-400 mt-0.5">{card.sub}</div>}
                </div>
                <span className={`grid place-items-center w-10 h-10 rounded-xl bg-white/60 shrink-0 ${c.text}`}><Icon name={card.icon} className="w-5 h-5" /></span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Profitability */}
      {profitability && <ProfitabilitySection data={profitability} />}

      {/* Best Sellers */}
      {bestSellers.length > 0 && <BestSellersSection sellers={bestSellers} />}

      {/* Pending Shipments */}
      <PendingShipmentsTable shipments={pendingShipments} />

      {/* Inventory Alerts */}
      {inventoryAlerts.length > 0 && <InventoryAlertsSection alerts={inventoryAlerts} />}

    </div>
  );
}

// ─── Categories manager ─────────────────────────────────────────────────────
// CRUD for the storefront catalogue categories. Editing here updates the
// Catalogue dropdown, the Shop filters, the product editor pills and the
// homepage carousels everywhere (refreshCategories() rebroadcasts the list).

// Click-to-apply theme swatches so a new category looks intentional without the
// owner having to hand-write a CSS gradient.
const CATEGORY_THEMES = [
  { name: 'Blush',   grad: 'linear-gradient(135deg,#ffe3ef 0%,#ffbcd9 100%)', ink: '#9d1d54' },
  { name: 'Sky',     grad: 'linear-gradient(135deg,#ecf6ff 0%,#c5e2fb 100%)', ink: '#1b4a78' },
  { name: 'Mint',    grad: 'linear-gradient(135deg,#e9f8ef 0%,#c7ecd5 100%)', ink: '#1c5e35' },
  { name: 'Ocean',   grad: 'linear-gradient(135deg,#e9f1ff 0%,#c9ddfb 100%)', ink: '#143f73' },
  { name: 'Lilac',   grad: 'linear-gradient(135deg,#f3f0ff 0%,#ddd6fe 100%)', ink: '#5b3aa6' },
  { name: 'Peach',   grad: 'linear-gradient(135deg,#fff1e6 0%,#ffd9b8 100%)', ink: '#9a4a14' },
  { name: 'Gold',    grad: 'linear-gradient(135deg,#fff8e1 0%,#ffe9a8 100%)', ink: '#7a5a00' },
  { name: 'Rose',    grad: 'linear-gradient(135deg,#ffeaf0 0%,#f7c6d6 100%)', ink: '#8a1c43' },
];

const ICON_OPTIONS = [...ICON_KEYS, '__generic__'];

// Editable card for one category (existing or a brand-new draft).
function CategoryCard({ initial, isNew, onSaved, onCancel, onDeleted }) {
  const blank = {
    label: '', value: '', blurb: '',
    grad: DEFAULT_GRAD, ink: DEFAULT_INK, icon: '__generic__',
    isActive: true,
  };
  const [form, setForm] = useState(() => ({
    ...blank,
    ...(initial || {}),
    icon: (initial?.icon && ICON_KEYS.includes(initial.icon)) ? initial.icon : '__generic__',
  }));
  const [saving, setSaving] = useState(false);
  const set = (f, v) => setForm(prev => ({ ...prev, [f]: v }));

  const save = async () => {
    if (!form.label.trim()) { toast.error('Give the category a name'); return; }
    setSaving(true);
    const payload = {
      label: form.label.trim(),
      blurb: form.blurb || null,
      grad: form.grad || null,
      ink: form.ink || null,
      icon: form.icon === '__generic__' ? null : form.icon,
      isActive: !!form.isActive,
    };
    // Only send an explicit slug when the owner typed/edited one.
    if (form.value && form.value.trim()) payload.value = form.value.trim();
    try {
      if (isNew) await api.post('/categories', payload);
      else       await api.put(`/categories/${initial.id}`, payload);
      await refreshCategories();
      toast.success(isNew ? 'Category added' : 'Category saved');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete “${initial.label}”? Products in it become uncategorised.`)) return;
    setSaving(true);
    try {
      await api.delete(`/categories/${initial.id}`);
      await refreshCategories();
      toast.success('Category deleted');
      onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Delete failed');
    } finally { setSaving(false); }
  };

  const iconKey = form.icon === '__generic__' ? '_generic_glyph_' : form.icon;

  return (
    <div className="bg-white border rounded-xl shadow-sm p-5 space-y-4">
      <div className="flex items-start gap-4">
        {/* Live preview chip */}
        <span className="grid place-items-center w-14 h-14 rounded-xl shrink-0 shadow-sm"
          style={{ background: form.grad || DEFAULT_GRAD }}>
          <CategoryIcon category={iconKey} style={{ width: 28, height: 28, color: form.ink || DEFAULT_INK }} />
        </span>
        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input className="input-field" value={form.label}
              onChange={e => set('label', e.target.value)} placeholder="e.g. Serums" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">
              URL key {isNew && <span className="text-gray-400">(auto from name)</span>}
            </label>
            <input className="input-field font-mono text-sm" value={form.value}
              onChange={e => set('value', e.target.value)} placeholder="serums" />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Blurb <span className="text-gray-400">(shown under the homepage carousel)</span></label>
        <input className="input-field" value={form.blurb}
          onChange={e => set('blurb', e.target.value)}
          placeholder="A one-line description of this collection." />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Theme */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Colour theme</label>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_THEMES.map(t => {
              const active = form.grad === t.grad && form.ink === t.ink;
              return (
                <button key={t.name} type="button" title={t.name}
                  onClick={() => { set('grad', t.grad); set('ink', t.ink); }}
                  className={'w-8 h-8 rounded-lg border-2 transition-transform ' +
                    (active ? 'border-gray-800 scale-110' : 'border-white shadow hover:scale-105')}
                  style={{ background: t.grad }} />
              );
            })}
          </div>
        </div>
        {/* Icon */}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1.5">Icon</label>
          <div className="flex flex-wrap gap-2">
            {ICON_OPTIONS.map(key => {
              const active = form.icon === key;
              const glyph = key === '__generic__' ? '_generic_glyph_' : key;
              return (
                <button key={key} type="button"
                  onClick={() => set('icon', key)}
                  title={key === '__generic__' ? 'Generic' : key}
                  className={'grid place-items-center w-9 h-9 rounded-lg border transition-colors ' +
                    (active ? 'border-pink-500 bg-pink-50 text-pink-700' : 'border-gray-200 text-gray-500 hover:border-gray-300')}>
                  <CategoryIcon category={glyph} className="w-5 h-5" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-1 border-t">
        <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
          <input type="checkbox" checked={!!form.isActive}
            onChange={e => set('isActive', e.target.checked)} />
          Visible on the storefront
        </label>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button type="button" onClick={remove} disabled={saving}
              className="text-sm text-red-600 hover:text-red-700 font-medium px-3 py-1.5">Delete</button>
          )}
          {(isNew || onCancel) && (
            <button type="button" onClick={onCancel} disabled={saving}
              className="btn-secondary text-sm">Cancel</button>
          )}
          <button type="button" onClick={save} disabled={saving}
            className="btn-primary text-sm">{saving ? 'Saving…' : (isNew ? 'Add category' : 'Save')}</button>
        </div>
      </div>
    </div>
  );
}

function CategoryManager() {
  const [cats, setCats] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/categories/all');
      setCats(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load categories');
      setCats([]);
    }
  };
  useEffect(() => { load(); }, []);

  const afterChange = async () => { setAdding(false); setEditingId(null); await load(); };

  // Reorder by swapping sortOrder with the neighbour, then persist both.
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length || busy) return;
    setBusy(true);
    const a = cats[idx], b = cats[j];
    try {
      await Promise.all([
        api.put(`/categories/${a.id}`, { label: a.label, sortOrder: b.sortOrder }),
        api.put(`/categories/${b.id}`, { label: b.label, sortOrder: a.sortOrder }),
      ]);
      await refreshCategories();
      await load();
    } catch {
      toast.error('Could not reorder');
    } finally { setBusy(false); }
  };

  if (!cats) return <div className="py-12 text-center text-gray-400">Loading…</div>;

  return (
    <div className="max-w-3xl">
      <div className="flex items-start justify-between gap-4 mb-2">
        <h2 className="text-2xl font-bold">Categories</h2>
        {!adding && (
          <button onClick={() => { setAdding(true); setEditingId(null); }}
            className="btn-primary text-sm whitespace-nowrap">+ Add category</button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-6">
        These are the catalogue collections shoppers browse. Adding, renaming, reordering
        or hiding one updates the <strong>Catalogue</strong> dropdown, the Shop filters, the
        product editor and the homepage carousels everywhere — no redeploy needed.
      </p>

      {adding && (
        <div className="mb-5">
          <CategoryCard isNew onSaved={afterChange} onCancel={() => setAdding(false)} />
        </div>
      )}

      <div className="space-y-3">
        {cats.length === 0 && !adding && (
          <div className="text-sm text-gray-400 bg-gray-50 border rounded-xl p-8 text-center">
            No categories yet. Add your first collection above.
          </div>
        )}
        {cats.map((c, idx) => (
          editingId === c.id ? (
            <CategoryCard key={c.id} initial={c}
              onSaved={afterChange} onCancel={() => setEditingId(null)} onDeleted={afterChange} />
          ) : (
            <div key={c.id} className={'bg-white border rounded-xl shadow-sm p-4 flex items-center gap-4 ' +
              (c.isActive ? '' : 'opacity-60')}>
              <span className="grid place-items-center w-11 h-11 rounded-lg shrink-0"
                style={{ background: c.grad || DEFAULT_GRAD }}>
                <CategoryIcon category={c.icon || c.value} style={{ width: 22, height: 22, color: c.ink || DEFAULT_INK }} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 flex items-center gap-2">
                  {c.label}
                  <span className="font-mono text-[11px] text-gray-400">/{c.value}</span>
                  {!c.isActive && <span className="text-[10px] uppercase tracking-wide bg-gray-200 text-gray-600 rounded px-1.5 py-0.5">Hidden</span>}
                </div>
                {c.blurb && <div className="text-xs text-gray-500 truncate">{c.blurb}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => move(idx, -1)} disabled={idx === 0 || busy}
                  className="w-7 h-7 grid place-items-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" aria-label="Move up">↑</button>
                <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1 || busy}
                  className="w-7 h-7 grid place-items-center rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30" aria-label="Move down">↓</button>
                <button onClick={() => { setEditingId(c.id); setAdding(false); }}
                  className="btn-secondary text-sm ml-1">Edit</button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Nav + Router ──────────────────────────────────────────────────────────────

const NAV_GROUPS = [
  { title: null, items: [
    { path: '/admin',               label: 'Overview',        icon: 'overview', exact: true },
  ]},
  { title: 'Sales', items: [
    { path: '/admin/orders',        label: 'Orders',          icon: 'orders' },
    { path: '/admin/commissions',   label: 'Commissions',     icon: 'commissions' },
  ]},
  { title: 'Catalog', items: [
    { path: '/admin/products',      label: 'Products',        icon: 'products' },
    { path: '/admin/categories',    label: 'Categories',      icon: 'gem' },
    { path: '/admin/logistics',     label: 'Logistics',       icon: 'logistics' },
    { path: '/admin/forecast',      label: 'Profit Forecast', icon: 'forecast' },
    { path: '/admin/pricing',       label: 'Price Scheduler', icon: 'pricing' },
  ]},
  { title: 'Growth', items: [
    { path: '/admin/marketing',     label: 'Marketing',       icon: 'marketing' },
    { path: '/admin/ambassadors',   label: 'Ambassadors',     icon: 'ambassadors' },
    { path: '/admin/coupons',       label: 'Coupons',         icon: 'coupons' },
    { path: '/admin/rewards',       label: 'Rewards',         icon: 'gift' },
    { path: '/admin/analytics',     label: 'Analytics',       icon: 'analytics' },
  ]},
  { title: 'Content', items: [
    { path: '/admin/press',         label: 'Press / Blog',    icon: 'press' },
  ]},
  { title: 'People', items: [
    { path: '/admin/users',         label: 'Users',           icon: 'users' },
  ]},
  { title: 'System', items: [
    { path: '/admin/coa',           label: 'COAs',            icon: 'coa' },
    { path: '/admin/media',         label: 'Media',           icon: 'media' },
    { path: '/admin/backup',        label: 'Backup',          icon: 'backup' },
    { path: '/admin/maintenance',   label: 'Maintenance',     icon: 'maintenance' },
  ]},
];

// Flat list — single source of truth for active-section lookups.
const NAV = NAV_GROUPS.flatMap(g => g.items);

// The four primary destinations pinned to the mobile bottom tab bar; everything
// else lives behind the "More" button, which opens the full grouped drawer.
const BOTTOM_NAV = [
  { path: '/admin',          label: 'Home',     icon: 'overview', exact: true },
  { path: '/admin/orders',   label: 'Orders',   icon: 'orders' },
  { path: '/admin/users',    label: 'Users',    icon: 'users' },
  { path: '/admin/products', label: 'Products', icon: 'products' },
];

function navItemActive(item, pathname) {
  return item.exact ? pathname === item.path : pathname.startsWith(item.path);
}

// Grouped link list, shared by the desktop sidebar and the mobile drawer.
// Fynix-style: uppercase section labels, soft rounded rows, and a pink pill for
// the active destination (icon tinted, faint ring) rather than a solid bar.
function GroupedNavList({ pathname, isBlack, onNavigate }) {
  return (
    <>
      {NAV_GROUPS.map((group, gi) => (
        <div key={group.title || gi} className="space-y-px">
          {group.title && (
            <div className={'px-3 pt-2.5 pb-0.5 text-[10px] font-bold uppercase tracking-[0.14em] ' +
              (isBlack ? 'text-gray-500' : 'text-gray-400')}>
              {group.title}
            </div>
          )}
          {group.items.map(n => {
            const active = navItemActive(n, pathname);
            return (
              <Link key={n.path} to={n.path} onClick={onNavigate}
                className={'group flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg text-sm transition-all duration-150 ' +
                  (active
                    ? (isBlack
                        ? 'bg-tea-600 text-white font-semibold shadow-sm shadow-tea-900/40'
                        : 'bg-tea-50 text-tea-700 font-semibold ring-1 ring-tea-100')
                    : (isBlack
                        ? 'text-gray-400 hover:bg-gray-800 hover:text-gray-100 font-medium'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900 font-medium'))}>
                <span className={'grid place-items-center w-7 h-7 rounded-lg transition-colors ' +
                  (active
                    ? (isBlack ? 'bg-white/15 text-white' : 'bg-white shadow-sm text-tea-600')
                    : (isBlack ? 'bg-gray-800/60 text-gray-400 group-hover:bg-gray-700 group-hover:text-gray-200' : 'bg-gray-100/70 text-gray-400 group-hover:bg-white group-hover:shadow-sm group-hover:text-tea-500'))}>
                  <Icon name={n.icon} className="w-4 h-4" />
                </span>
                <span className="truncate">{n.label}</span>
              </Link>
            );
          })}
        </div>
      ))}
    </>
  );
}

// Initials for the avatar bubbles (account chip + header).
function initialsOf(user) {
  const a = (user?.firstName || '').trim();
  const b = (user?.lastName || '').trim();
  const init = (a[0] || '') + (b[0] || '');
  return (init || (user?.email || 'A')[0] || 'A').toUpperCase();
}

// Pink gradient brand mark + wordmark, pinned to the top of the sidebar.
function SidebarBrand({ isBlack }) {
  return (
    <Link to="/admin" className="flex items-center gap-2.5 px-2 py-1 group">
      <span className="relative grid place-items-center h-9 w-9 rounded-xl bg-gradient-to-br from-tea-400 to-tea-600 shadow-md shadow-tea-500/30 overflow-hidden">
        <span className="absolute -right-2 -top-2 h-6 w-6 rounded-full bg-white/25 blur-[2px]" />
        <BrandMark className="relative w-5 h-5 text-white" />
      </span>
      <span className="flex flex-col leading-tight">
        <span className={'font-extrabold text-[15px] tracking-tight ' + (isBlack ? 'text-white' : 'text-gray-900')}>AB8 Beauty</span>
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-tea-500">Admin Suite</span>
      </span>
    </Link>
  );
}

// Account chip with a dropdown menu — avatar + name + role, opening a small
// popover with storefront links and the log-out action. This is the primary
// way an admin signs out on desktop.
function AccountChip({ user, isBlack, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const itemCls = 'flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg transition-colors text-left ' +
    (isBlack ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900');

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className={'flex items-center gap-3 w-full rounded-2xl px-3 py-2.5 border transition-colors ' +
          (isBlack
            ? 'bg-gray-900 border-gray-800 hover:border-gray-700'
            : 'bg-white border-gray-200 hover:border-tea-200 hover:shadow-sm')}>
        <span className="grid place-items-center h-9 w-9 rounded-full bg-gradient-to-br from-tea-400 to-tea-600 text-white text-xs font-bold shadow-sm shrink-0">
          {initialsOf(user)}
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span className={'block text-sm font-bold truncate ' + (isBlack ? 'text-gray-100' : 'text-gray-900')}>
            {user?.firstName || 'Admin'} {user?.lastName || ''}
          </span>
          <span className="block text-[11px] text-gray-400 truncate">Admin Account</span>
        </span>
        <Icon name="selector" className={'w-4 h-4 shrink-0 transition-colors ' + (open ? 'text-tea-500' : 'text-gray-300')} />
      </button>

      {open && (
        <div className={'absolute left-0 right-0 top-full mt-2 z-40 rounded-2xl border p-1.5 shadow-xl ' +
          (isBlack ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100')}>
          <Link to="/account" onClick={() => setOpen(false)} className={itemCls}>
            <Icon name="users" className="w-4 h-4 text-gray-400" /> Storefront account
          </Link>
          <Link to="/" onClick={() => setOpen(false)} className={itemCls}>
            <Icon name="back" className="w-4 h-4 text-gray-400" /> Back to storefront
          </Link>
          <div className={'my-1 border-t ' + (isBlack ? 'border-gray-800' : 'border-gray-100')} />
          <button onClick={() => { setOpen(false); onLogout(); }}
            className="flex items-center gap-2.5 w-full px-3 py-2 text-sm rounded-lg transition-colors text-left text-rose-500 hover:bg-rose-50 font-semibold">
            <Icon name="logout" className="w-4 h-4" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

// Sticky content header: section title + tagline on the left, a search pill and
// quick-action icons + avatar on the right — the Fynix "Home" top bar.
const SECTION_TAGLINES = {
  Overview: 'Track your store with live insights and recommendations.',
  Orders: 'Fulfil, track and message customers from one place.',
  Users: 'God-tier control over every account and downline.',
  Products: 'Curate the catalog, stock and pricing.',
};
function AdminHeader({ title, user, isBlack }) {
  const tagline = SECTION_TAGLINES[title] || 'Manage your AB8 Beauty storefront.';
  return (
    <header className={'sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-4 mb-6 backdrop-blur-md border-b ' +
      (isBlack ? 'bg-black/70 border-gray-800' : 'bg-white/70 border-gray-100')}>
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className={'text-2xl font-extrabold tracking-tight truncate ' + (isBlack ? 'text-white' : 'text-gray-900')}>{title}</h1>
          <p className="text-[13px] text-gray-400 mt-0.5 truncate italic">&ldquo;{tagline}&rdquo;</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className={'hidden lg:flex items-center gap-2 rounded-full px-4 py-2 w-64 border ' +
            (isBlack ? 'bg-gray-900 border-gray-800' : 'bg-gray-50 border-gray-200')}>
            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
            </svg>
            <input
              placeholder="Search anything"
              className={'flex-1 bg-transparent text-sm focus:outline-none placeholder:text-gray-400 ' + (isBlack ? 'text-gray-100' : 'text-gray-700')}
            />
            <span className="text-[10px] font-semibold text-gray-400 border border-gray-300 rounded px-1.5 py-0.5">⌘F</span>
          </div>
          <Link to="/admin/maintenance" aria-label="Settings"
            className={'grid place-items-center h-10 w-10 rounded-full border transition-colors ' +
              (isBlack ? 'bg-gray-900 border-gray-800 text-gray-300 hover:text-white' : 'bg-white border-gray-200 text-gray-500 hover:text-tea-600 hover:border-tea-200')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </Link>
          <span className="grid place-items-center h-10 w-10 rounded-full bg-gradient-to-br from-tea-400 to-tea-600 text-white text-xs font-bold shadow-sm">
            {initialsOf(user)}
          </span>
        </div>
      </div>
    </header>
  );
}

// Fixed bottom tab bar (mobile only): four primary tabs plus a "More" trigger.
function AdminBottomNav({ pathname, isBlack, onMore }) {
  return (
    <nav className={'md:hidden fixed bottom-0 inset-x-0 z-30 border-t flex shadow-[0_-1px_4px_rgba(0,0,0,0.08)] ' +
      (isBlack ? 'bg-black border-gray-800' : 'bg-white')}>
      {BOTTOM_NAV.map(n => {
        const active = navItemActive(n, pathname);
        return (
          <Link key={n.path} to={n.path}
            className={'flex-1 flex flex-col items-center justify-center py-2 gap-1 text-[11px] font-medium ' +
              (active ? 'text-tea-600' : isBlack ? 'text-gray-400' : 'text-gray-500')}>
            <Icon name={n.icon} className="w-[22px] h-[22px]" strokeWidth={active ? 2 : 1.7} />
            {n.label}
          </Link>
        );
      })}
      <button onClick={onMore}
        className={'flex-1 flex flex-col items-center justify-center py-2 gap-1 text-[11px] font-medium ' +
          (isBlack ? 'text-gray-400' : 'text-gray-500')}
        aria-label="More admin sections">
        <Icon name="menu" className="w-[22px] h-[22px]" />
        More
      </button>
    </nav>
  );
}

// Slide-in drawer (mobile only) holding the full grouped navigation, with a
// brand header and a footer carrying the storefront link + log-out action.
function AdminNavDrawer({ open, onClose, pathname, isBlack, user, onLogout }) {
  return (
    <div className={'md:hidden fixed inset-0 z-50 ' + (open ? '' : 'pointer-events-none')}>
      <div onClick={onClose}
        className={'absolute inset-0 bg-black/40 transition-opacity duration-200 ' + (open ? 'opacity-100' : 'opacity-0')} />
      <div className={'absolute left-0 top-0 h-full w-72 max-w-[82vw] shadow-2xl flex flex-col transition-transform duration-200 ' +
        (open ? 'translate-x-0' : '-translate-x-full') + ' ' + (isBlack ? 'bg-black' : 'bg-white')}>
        <div className={'px-4 pt-5 pb-3 border-b flex items-center justify-between ' + (isBlack ? 'border-gray-800' : 'border-gray-100')}>
          <SidebarBrand isBlack={isBlack} />
          <button onClick={onClose} aria-label="Close menu"
            className={'grid place-items-center h-8 w-8 rounded-full ' + (isBlack ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700')}>
            <Icon name="close" className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-3">
          <GroupedNavList pathname={pathname} isBlack={isBlack} onNavigate={onClose} />
        </nav>
        <div className={'p-3 border-t space-y-1 ' + (isBlack ? 'border-gray-800' : 'border-gray-100')}>
          <Link to="/" onClick={onClose}
            className={'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium ' +
              (isBlack ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-50')}>
            <Icon name="back" className="w-[18px] h-[18px] text-gray-400" /> Back to storefront
          </Link>
          <button onClick={() => { onClose(); onLogout(); }}
            className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-sm font-semibold text-rose-500 hover:bg-rose-50 transition-colors">
            <Icon name="logout" className="w-[18px] h-[18px]" /> Log out
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Unique-visitor gong ──────────────────────────────────────────────────────
// Rings a gong for logged-in admins whenever a NEW unique visitor lands on the
// storefront. Polls the analytics arrivals feed (see routes/analytics.js); the
// gong is synthesised with the Web Audio API so there is no audio asset to ship.
// The on/off state is per-admin (localStorage) and the toggle click doubles as
// the user gesture that unlocks browser autoplay.
const GONG_LS_KEY = "tt_admin_gong_enabled";

function playGong(ctx, volume = 0.9) {
  const now = ctx.currentTime;

  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(volume, now + 0.012);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 3.6);

  // A slowly-closing low-pass gives the gong its decaying shimmer.
  const lp = ctx.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.setValueAtTime(5200, now);
  lp.frequency.exponentialRampToValueAtTime(650, now + 3.4);
  lp.connect(master);
  master.connect(ctx.destination);

  // Inharmonic metallic partials = the bronze, gong-like timbre.
  const partials = [
    [82.41, 0.9], [110.0, 0.7], [164.8, 0.55], [220.0, 0.5], [277.2, 0.4],
    [329.6, 0.32], [440.0, 0.25], [587.3, 0.18], [739.9, 0.12],
  ];
  for (const [freq, g] of partials) {
    const osc = ctx.createOscillator();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq * (1 + (g - 0.5) * 0.004), now); // slight detune
    const og = ctx.createGain();
    og.gain.setValueAtTime(g, now);
    og.gain.exponentialRampToValueAtTime(0.0001, now + 2.4 + g);
    osc.connect(og); og.connect(lp);
    osc.start(now); osc.stop(now + 3.7);
  }

  // Short filtered noise burst = the mallet strike transient.
  const noiseLen = Math.floor(ctx.sampleRate * 0.18);
  const buf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  const ch = buf.getChannelData(0);
  for (let i = 0; i < noiseLen; i++) ch[i] = (Math.random() * 2 - 1) * (1 - i / noiseLen);
  const noise = ctx.createBufferSource();
  noise.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass"; bp.frequency.value = 1200;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0.5 * volume, now);
  ng.gain.exponentialRampToValueAtTime(0.0001, now + 0.2);
  noise.connect(bp); bp.connect(ng); ng.connect(master);
  noise.start(now); noise.stop(now + 0.2);
}

function GongNotifier() {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(GONG_LS_KEY) === "1"; } catch { return false; }
  });
  const [flash, setFlash] = useState(null);
  const ctxRef = useRef(null);
  const sinceRef = useRef(null);
  const pollRef = useRef(null);
  const flashTimer = useRef(null);

  // Lazily create / resume the AudioContext (must follow a user gesture).
  const ensureAudio = () => {
    try {
      if (!ctxRef.current) {
        const AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return null;
        ctxRef.current = new AC();
      }
      if (ctxRef.current.state === "suspended") ctxRef.current.resume().catch(() => {});
      return ctxRef.current;
    } catch { return null; }
  };

  // If the gong was left ON from a previous session, the browser auto-suspends
  // audio until a gesture — re-unlock on the first interaction anywhere.
  useEffect(() => {
    if (!enabled) return undefined;
    const unlock = () => ensureAudio();
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [enabled]);

  // Poll the arrivals feed while enabled.
  useEffect(() => {
    if (!enabled) {
      if (pollRef.current) clearInterval(pollRef.current);
      sinceRef.current = null;
      return undefined;
    }
    let stopped = false;
    const poll = async () => {
      try {
        const params = sinceRef.current != null ? { since: sinceRef.current } : {};
        const { data } = await api.get("/admin/analytics/arrivals", { params });
        if (stopped) return;
        const firstSync = sinceRef.current == null;
        sinceRef.current = data.seq;
        if (firstSync) return; // sync only — do not gong for the backlog
        const arr = data.arrivals || [];
        if (arr.length) {
          const ctx = ensureAudio();
          if (ctx) playGong(ctx);
          const a = arr[arr.length - 1];
          const place = [a.city, a.region, a.country].filter(Boolean).join(", ");
          const label = arr.length > 1
            ? arr.length + " new visitors just landed"
            : (a.isNewVisitor ? "New visitor" : "Visitor") + (place ? " · " + place : "");
          setFlash({ label, at: Date.now() });
          if (flashTimer.current) clearTimeout(flashTimer.current);
          flashTimer.current = setTimeout(() => setFlash(null), 6000);
        }
      } catch { /* ignore transient errors (401 on logout, network blips) */ }
    };
    poll();
    pollRef.current = setInterval(poll, 5000);
    return () => { stopped = true; clearInterval(pollRef.current); };
  }, [enabled]);

  useEffect(() => () => { if (flashTimer.current) clearTimeout(flashTimer.current); }, []);

  const toggle = () => {
    setEnabled(prev => {
      const next = !prev;
      try { localStorage.setItem(GONG_LS_KEY, next ? "1" : "0"); } catch {}
      if (next) {
        const ctx = ensureAudio();
        if (ctx) playGong(ctx, 0.45); // soft confirmation tap so they know it works
      }
      return next;
    });
  };

  return (
    <>
      {flash && (
        <div className="fixed left-4 bottom-32 md:bottom-20 z-40">
          <div className="flex items-center gap-2 rounded-full bg-amber-500 text-white shadow-lg px-4 py-2 text-sm font-semibold animate-bounce">
            <Icon name="bell" className="w-4 h-4" />
            {flash.label}
          </div>
        </div>
      )}
      <button onClick={toggle}
        title={enabled ? "Visitor gong is ON — click to mute" : "Visitor gong is OFF — click to enable"}
        aria-label={enabled ? "Mute new-visitor gong" : "Enable new-visitor gong"}
        className={"fixed left-4 bottom-20 md:bottom-6 z-40 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold shadow-lg transition-colors " +
          (enabled ? "bg-tea-600 text-white hover:bg-tea-700" : "bg-white text-gray-600 border border-gray-300 hover:bg-gray-50")}>
        <Icon name={enabled ? 'bell' : 'bellOff'} className="w-4 h-4" />
        <span className="hidden sm:inline">Gong {enabled ? "On" : "Off"}</span>
      </button>
    </>
  );
}

export default function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthCtx();
  const isBlack = user?.email === 'black@ab8beauty.com';
  const [drawerOpen, setDrawerOpen] = useState(false);
  const activeName = NAV.find(n => navItemActive(n, location.pathname))?.label || 'Admin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Close the mobile drawer whenever navigation moves to a new section.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Mark admin "online" so customers waiting on a payment confirmation see
  // the green dot in the chat. Heartbeat fires immediately + every 30s.
  useEffect(() => {
    const ping = () => api.post('/admin/heartbeat').catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className={'min-h-screen ' + (isBlack ? 'bg-black' : 'bg-[#faf7f9]')}>
      {/* Mobile top bar: brand + current section + drawer trigger */}
      <div className={'md:hidden sticky top-0 z-30 border-b px-4 py-3 flex items-center gap-3 ' +
        (isBlack ? 'bg-black border-gray-800' : 'bg-white/80 backdrop-blur-md border-gray-100')}>
        <button onClick={() => setDrawerOpen(true)}
          className={'flex items-center gap-2 font-semibold ' + (isBlack ? 'text-gray-100' : 'text-gray-800')}
          aria-label="Open admin menu">
          <Icon name="menu" className="w-6 h-6" />
          <span>{activeName}</span>
        </button>
        <span className="ml-auto grid place-items-center h-8 w-8 rounded-full bg-gradient-to-br from-tea-400 to-tea-600 text-white text-[11px] font-bold">
          {initialsOf(user)}
        </span>
      </div>

      {/* Mobile slide-in drawer with the full grouped nav */}
      <AdminNavDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)}
        pathname={location.pathname} isBlack={isBlack} user={user} onLogout={handleLogout} />

      {/* Fixed desktop sidebar — Fynix-style brand / account / grouped nav */}
      <aside className={'hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r z-30 ' +
        (isBlack ? 'bg-black border-gray-800' : 'bg-white border-gray-100')}>
        <div className="px-4 pt-3 pb-2">
          <SidebarBrand isBlack={isBlack} />
        </div>
        <div className="px-4 pb-2">
          <AccountChip user={user} isBlack={isBlack} onLogout={handleLogout} />
        </div>
        <div className={'mx-4 mb-1 border-t ' + (isBlack ? 'border-gray-800' : 'border-gray-100')} />
        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          <GroupedNavList pathname={location.pathname} isBlack={isBlack} />
        </nav>
        <div className={'px-3 py-3 border-t space-y-0.5 ' + (isBlack ? 'border-gray-800' : 'border-gray-100')}>
          <Link to="/" className={'flex items-center gap-3 text-xs font-semibold rounded-lg px-3 py-2 transition-colors ' +
            (isBlack ? 'text-gray-400 hover:bg-gray-900' : 'text-gray-500 hover:bg-gray-50 hover:text-tea-700')}>
            <Icon name="back" className="w-4 h-4" /> Back to storefront
          </Link>
          <button onClick={handleLogout}
            className="flex items-center gap-3 w-full text-xs font-semibold rounded-lg px-3 py-2 text-rose-500 hover:bg-rose-50 transition-colors">
            <Icon name="logout" className="w-4 h-4" /> Log out
          </button>
        </div>
      </aside>

      {/* Main content column, offset by the fixed sidebar on desktop */}
      <div className="md:pl-64 min-h-screen flex flex-col">
        <main className="flex-1 min-w-0 px-4 sm:px-6 lg:px-8 pb-24 md:pb-10">
          <AdminHeader title={activeName === 'Admin' ? 'Overview' : activeName} user={user} isBlack={isBlack} />
          <Routes>
            <Route index                   element={<BoundedRoute element={<MetricsOverview />} />} />
            <Route path="products"         element={<BoundedRoute element={<ProductsList />} />} />
            <Route path="categories"       element={<BoundedRoute element={<CategoryManager />} />} />
            <Route path="orders"           element={<BoundedRoute element={<OrdersList />} />} />
            <Route path="logistics"        element={<BoundedRoute element={<LogisticsCenter />} />} />
            <Route path="users"            element={<BoundedRoute element={<UsersList />} />} />
            <Route path="commissions"      element={<BoundedRoute element={<CommissionsList />} />} />
            <Route path="analytics"        element={<BoundedRoute element={<AnalyticsPage />} />} />
            <Route path="marketing"        element={<BoundedRoute element={<MarketingPage />} />} />
            <Route path="ambassadors"      element={<BoundedRoute element={<AmbassadorInvites />} />} />
            <Route path="coupons"          element={<BoundedRoute element={<CouponsPage />} />} />
            <Route path="press"            element={<BoundedRoute element={<BlogManager />} />} />
            <Route path="coa"              element={<BoundedRoute element={<COAManager />} />} />
            <Route path="media"            element={<BoundedRoute element={<MediaLibrary />} />} />
            <Route path="backup"           element={<BoundedRoute element={<BackupExport />} />} />
            <Route path="forecast"         element={<BoundedRoute element={<ForecastPage />} />} />
            <Route path="pricing"          element={<BoundedRoute element={<PriceSchedulerPage />} />} />
            <Route path="rewards"          element={<BoundedRoute element={<RewardSettings />} />} />
            <Route path="maintenance"      element={<BoundedRoute element={<MaintenanceSettings />} />} />
          </Routes>
        </main>
      </div>

      {/* Mobile bottom tab bar: primary destinations + More */}
      <AdminBottomNav pathname={location.pathname} isBlack={isBlack}
        onMore={() => setDrawerOpen(true)} />

      <AdminChatDock />
      <GongNotifier />
    </div>
  );
}
